/**
 * CheckoutFlow.tsx — plan → delivery & payment (ONE step) → account, then OUT
 * to /thank-you.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * DELIVERY AND PAYMENT MERGED INTO ONE CONTINUOUS SECTION (CTO, 2026-09-23,
 * call with Ashley): after the plan, the patient enters the delivery address
 * and the card on the SAME screen — the address form at the top, and once the
 * address is saved the order summary, the safety gate and the card form appear
 * BELOW it (progressive disclosure, no step navigation between them). And at
 * card entry they SEE what they are paying for: the summary now carries the
 * plan's monthly price and the server-priced term total ("in any checkout
 * process, whenever they are adding the card, they should see what they are
 * paying for and what the amount is"). The session choreography did NOT move:
 * the safety tick still mints the checkout session with the in-page address,
 * exactly as below.
 *
 * ONCE SAVED, THE ADDRESS COLLAPSES (review, 2026-09-23). The first cut kept
 * the address form mounted and EDITABLE under the payment half, and that was a
 * money defect, not a cosmetic one: a keystroke while a card confirm was in
 * flight made the form dirty, its unsaved-changes guard then BLOCKED the
 * `/thank-you` navigation of an order that had just been placed, and "Stay
 * here" brought back a live pay control for it — a second press minted a
 * second order and a second hold. So a saved address renders as a read-only
 * "Deliver to" block with a Change control. The form exists only while it is
 * the thing being worked on (first entry, or after Change), which also means no
 * pay control exists while it does. Change is LOCKED while any payment work is
 * in flight — including Stripe's `confirmSetup`, which `PaymentElementsForm`
 * now reports through `onSubmittingChange` — so no address change can start
 * against a confirm that has already captured the old session.
 *
 * AND THE MONEY IS THE FIGURE ORDERS WILL HOLD. Which server that is depends
 * on the lane — orders re-quotes at session mint on the hold route, and has
 * already fixed the price on the clinician-led plan and on a resumed session.
 * The rule lives in `checkout/cardStepPrice.ts`; every money figure on this
 * screen (and the funnel's `add_payment_info` amount) reads that one answer.
 *
 * CHECKOUT NO LONGER ENDS IN THIS FILE (2026-08-20). The confirmation was a
 * component swap on a `stage === 'confirm'` branch, so the address bar never
 * moved and marketing had no URL change to fire a conversion on. It is now the
 * `/thank-you` ROUTE (`checkout/ThankYouPage.tsx`); every completion path here
 * exits through the one `goToConfirmation` below. The event that fires there is
 * `checkout_complete` and deliberately NOT `purchase` — on the hold route no
 * money has been taken at that point and the real, deduped revenue event fires
 * server-side on capture.
 *
 * AND EVERY STAGE CHANGE RESETS THE VIEW (same date, same CTO report): swapping
 * a tall payment screen for a short account screen inside one route used to
 * leave the window scrolled past all of the new content, so the moment after
 * paying looked like a blank page. See `useResetViewOnChange` below.
 *
 * WHY: after an eligible result the patient converts here. Guests select a plan,
 * enter delivery, and pay BEFORE creating / signing into an account; the
 * assessment-bound `checkoutToken` authorises guest Orders calls. After
 * attachPaymentMethod, Register/Login claims the OrderCase. Signed-in patients
 * skip the account stage only when `emailVerified` is true; an unverified
 * session waits on the emailed link before confirmation (dashboard is gated
 * the same way).
 *
 * Payment is two-phase against Orders:
 *   1. POST /v1/orders/checkout/session (or /guest/session) → SetupIntent grant
 *   2. Stripe.js confirmSetup (or FakeStripe stub) → POST …/payment-method
 * Route 1/2 confirmation copy follows server `paymentPhase` (`held` | `captured`),
 * not query params. Double-submit is guarded while initialising/confirming/finalising.
 *
 * A LOST ATTACH ANSWER IS ASKED AGAIN, NEVER REPORTED AS "NOT CHARGED" (journey
 * UX review, 2026-09-24). When step 2's POST reaches orders but its answer never
 * reaches the browser (a dropped connection, a 5xx), the month-1 hold may already
 * stand. The step says it could not CONFIRM the order (`payOutcomeUnknown`), and
 * the next press of the same control re-attaches the SAME SetupIntent — on the
 * Elements lane through Stripe's "this intent already succeeded" refusal
 * (`onSetupAlreadyConfirmed`), which only attaches an intent THIS window's
 * session names, and counts the answer only when THIS document confirmed that
 * card (`setupConfirmedHere`) — another tab of the same checkout shares the
 * session. orders answers that retry idempotently, so the payment is handed
 * over — and counted — once.
 *
 * THE SAFETY GATE MOVED STEP 1 (2026-08-19, clinician feedback — Reneal Patel).
 * `checkout/session` now carries `safetyAck`, the record that this patient was
 * shown what this prescription-only medicine can do to them, and orders-service
 * refuses a checkout without it. The acknowledgement cannot exist before the
 * payment screen renders the risks, so the session is no longer created when the
 * address form is submitted — Payment paints first, the gate renders above the
 * pay control, and the POST fires the moment the tick lands (see
 * `MedicationSafetyGate` and `prepareSession`). No new step, no extra press, and
 * no OrderCase for a medicine nobody explained.
 *
 * HARD REFRESH: CheckoutFlow calls GET checkout/resume (guest or PATIENT) and
 * restores the merged delivery-and-payment step (address-first, or with the
 * payment half revealed) / Account / confirmation from the server OrderCase.
 * Guest grant lives in sessionStorage keyed by assessmentId.
 *
 * Plan → the merged step persists via POST checkout/selection. The session via
 * POST checkout/session on the safety tick. Account stage: a new patient proves the assessment
 * email with the 6-digit code, then register consumes that OTP-minted token and
 * binds clinic Assessment.patientUserId. The public checkout grant authorises
 * guest Orders only — it is not identity and must not skip the code. Login
 * peeks the grant, email-matches, binds, then claim. Orders claim always
 * requires isBound.
 *
 * THE ACCOUNT STEP IS FOR NEW PATIENTS, AND FINDS OUT OTHERWISE FROM IDENTITY
 * (CTO 2026-09-24). *"Since this should only trigger for new patients, I don't
 * think we should give the sign-in option. If it is a patient who is already in
 * our database… detect that they already have an account and then prompt them
 * to sign in."* So there is no "Already have an account? Sign in" link. The
 * register form asks for a password twice (no DOB, no editable email), the
 * code gate follows, and when identity's register answers 409 ("An account with
 * this email already exists") the step switches IN PLACE to a sign-in view with
 * the address locked, which then binds (email match) and claims exactly as the
 * old sign-in link did.
 *   ⚠️ THAT 409 ARRIVES AFTER IDENTITY HAS REDEEMED THE CODE'S TOKEN. Register
 * consumes the single-use re-entry token before it reaches the unique-email
 * write, so the token that proved the code is BURNED by the time the conflict
 * comes back — and on the form-first lane that token had also replaced the
 * checkout grant. A sign-in that bound with it would be refused ("Unable to
 * link this assessment") after the patient had paid. `bindGrantToken` therefore
 * never reuses a token a register spent: it falls back to a grant this window
 * held before the code, and failing that mints a fresh one through the same
 * public guest-lane mint the journey already uses. That mint is no new
 * capability (anyone holding the assessment id can already call it); what
 * protects the bind is identity requiring the signed-in account's STORED email
 * to match the assessment's.
 *   ⚠️ AND THAT STORED EMAIL MAY NEVER HAVE BEEN PROVEN — the server fix is
 * identity#105 (`fix/bind-requires-verified-email`, OPEN 2026-09-24). On
 * identity `dev` today `bindAssessment` checks role + an email match and never
 * `emailVerified`; a `/auth` register creates an UNVERIFIED account with no
 * mailbox proof, and orders' claim needs only the bind — so someone who knows a
 * patient's email and assessment id could register that email first and attach
 * the patient's order (and card vault) to it. identity#105 refuses an
 * unverified PATIENT's bind with 403 `email_not_verified` (EmailVerifiedGuard's
 * code) BEFORE anything else, and reads the flag LIVE from the row. This file
 * answers that refusal as the email hold it is, not as a failure: `bindOwed`
 * survives it, the "Confirm your email" screen tells the patient to confirm and
 * COME BACK TO THIS PAGE (`BIND_WAITS_FOR_EMAIL`, no red alert), and the
 * email-hold effect re-binds and claims with the same session when
 * `me.emailVerified` flips. Every other bind refusal keeps the generic line.
 *   ⚠️ THAT OWED BIND LIVES IN THIS TAB ONLY. `bindOwed` is a ref and
 * `bindHeldForEmail` is state; the verify link binds and claims nothing, and a
 * reload / new tab / discarded mobile tab restores through the signed-in resume,
 * which asks orders by ACCOUNT and so finds nothing for a still-guest-owned
 * order (plan step). Hence the copy's "keep this page open". Carrying the owed
 * bind across a reload (sessionStorage beside the stored grant) is the durable
 * fix and is not built — see CLAUDE.md.
 *   ⚠️ RELEASE ORDER: identity#105 ships WITH or AFTER this portal batch, never
 * before it. dev's portal has no `bindOwed` — its email-hold retry calls claim
 * only — so identity#105 alone would refuse the bind, and orders would then
 * refuse every retried claim as unbound: a paid order stranded with nothing
 * telling the patient to sign out and back in. Until identity#105 is live, read
 * "email match" here as "matches an address that was TYPED", not proven.
 *   ⚠️ AND THE 409 MISSES A CASE-VARIANT ACCOUNT. identity stores an address
 * as typed and its unique index is case-sensitive, and register does no
 * case-insensitive pre-check — so a returning patient stored as
 * `Pat@x.com` whose assessment says `pat@x.com` gets no 409 here: a second
 * account is created and the order is claimed onto it. Identity-side fix.
 * There is no way to learn that the account exists any EARLIER without adding
 * an email-existence answer somewhere, which is the enumeration surface this
 * estate has closed everywhere else; see the CHANGELOG entry for the options.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ZoneExitLink } from '../tracking/ZoneExitLink';
import { trackAddPaymentInfo, trackAddToCart } from '../tracking/dataLayer';
import { toTrackingPlan } from '../tracking/planItem';
import { useAuth } from '../auth/AuthContext';
import { RegisterForm, LoginForm, type ExistingAccountConflict } from '../auth/forms';
import { CheckEmailCard } from '../auth/CheckEmailCard';
import { CheckoutOwnershipHold } from './CheckoutOwnershipHold';
import { checkoutOwnershipRefusal, EMAIL_NOT_VERIFIED, type CheckoutOwnershipRefusal } from './checkoutOwnership';
import { ApiError } from '../api/client';
import { getAssessmentAccountBootstrap, mintCheckoutToken, requestRestoreCode } from '../api/journey';
import {
  attachPaymentMethod,
  checkoutSession,
  claimCheckout,
  getCheckoutResume,
  getPlanPreview,
  retryCardAfterDecline,
  saveCheckoutSelection,
} from '../api/orders';
import { bindAssessment, getPatientProfile } from '../api/identity';
import { getPlanOptions } from '../api/prices';
import { formatAddress, formatPence } from '../lib/format';
import type {
  Address,
  AttachPaymentMethodResult,
  CheckoutSessionResult,
  PricePlan,
  SafetyAcknowledgement,
} from '../api/types';
import { requireStripePublishableKey } from '../config';
import { Alert, Button, Card, CheckIcon, ErrorState, Help, Title } from '../ui/kit';
import { BrandedLoader, useLoaderHold } from '../ui/BrandedLoader';
import { isNonProdHost } from '../ui/chrome';
import { RestoreCodeEntry } from '../journey/RestoreCodeEntry';
import { useResetViewOnChange } from '../ui/viewReset';
import { AddressForm } from './AddressForm';
import { CHECKOUT_LOADER_CARD, CHECKOUT_PREPLAN_MIN_DWELL_MS } from './checkoutLoaderContent';
import { PaymentElementsForm } from './PaymentElementsForm';
import {
  attachOutcomeUnknown,
  cardRetryUnavailable,
  CHECKOUT_PLAN_CHANGED_ELSEWHERE_MESSAGE,
  CHECKOUT_SAFETY_ACK_REQUIRED_MESSAGE,
  DECLINED_NO_DIFFERENT_CARD_MESSAGE,
  DECLINED_TRY_ANOTHER_CARD_MESSAGE,
  isDeclinedMonth1Hold,
  mapCheckoutApiError,
  mapStripePaymentError,
  PAYMENT_UNAVAILABLE_MESSAGE,
  staleCheckoutRefusal,
  type StaleCheckoutRefusal,
} from './paymentErrors';
import { decidePaymentSessionFlow, sameDeliveryAddress } from './paymentSession';
import {
  paymentCaptureRoute,
  paymentPhaseToCaptureRoute,
  paymentStepCopy,
  type PaymentCaptureRoute,
} from './paymentCopy';
import { CheckoutProgress } from './CheckoutProgress';
import { THANK_YOU_PATH, type ThankYouNavState } from './thankYouRoute';
import { noteCheckoutHandOver, usePostPaymentCelebration } from './Celebration';
import { AccountSteps, type AccountStepState } from './AccountSteps';
import { accountStageHelp } from './celebrationCopy';
import {
  BACK_TO_PASSWORD,
  BIND_WAITS_FOR_EMAIL,
  BIND_WAITS_RESEND_HINT,
  CODE_DELIVERY_HELP,
  createAccountHelp,
  EMAIL_CONFIRMED_STATUS,
  EXISTING_ACCOUNT_FORGOT,
  EXISTING_ACCOUNT_FORGOT_RETURN,
  EXISTING_ACCOUNT_REASON,
  EXISTING_ACCOUNT_TITLE,
  type AccountLane,
} from './accountStepCopy';
import { clearCheckoutToken, readCheckoutToken, writeCheckoutToken } from './checkoutTokenStorage';
import { PlanSelection, type InitialPlanChoice, type PlanChoice } from './PlanSelection';
import { planScreenShowsError, startPlanScreenRead, type EarlyPlanScreenRead } from './planScreenRead';
import {
  CHECKOUT_NOTICE_ID,
  CheckoutNoticeBlock,
  PaymentRail,
  PLAN_CHANGE_REFUSED_MESSAGE,
  type CheckoutNotice,
} from './PaymentRail';
import {
  MedicationSafetyGate,
  PAY_BLOCKED_REASON_ID,
  payBlockedReason,
  safetyAckFor,
  type MedicationSafetyView,
} from './MedicationSafetyGate';
import { cardStepPrice, type CardStepQuoteSource } from './cardStepPrice';
import {
  checkoutReturnUrl,
  redirectReturnAction,
  redirectSetupVerdict,
  type SetupRedirectResult,
} from './setupRedirectReturn';
import { getStripePromise } from './stripeClient';

/** The checkout AddressForm's id prefix — Change moves focus to its first field. */
const ADDRESS_ID_PREFIX = 'addr';
/** The collapsed "Deliver to" block — focus lands here when a saved address collapses. */
const SAVED_ADDRESS_ID = 'checkout-saved-address';
/** WHY Change is locked, in words, tied to the control by `aria-describedby`. */
const CHANGE_LOCKED_REASON_ID = 'checkout-change-address-locked';
/** The sign-in view's "you already have an account" sentence — the password field names it. */
const EXISTING_ACCOUNT_REASON_ID = 'checkout-existing-account-reason';
/** "…come back to this page and sign in here" — the forgotten-password link names it. */
const EXISTING_ACCOUNT_FORGOT_RETURN_ID = 'checkout-existing-account-forgot-return';
/** The form-first "your email is confirmed" line — focus lands here when the code box leaves. */
const EMAIL_CONFIRMED_ID = 'checkout-email-confirmed';
/** The register form's first password box (auth/forms.tsx) — where Back from the code box lands. */
const REGISTER_PASSWORD_ID = 'reg-password';
/** The sign-in view's password box (auth/forms.tsx). */
const LOGIN_PASSWORD_ID = 'login-password';
/** The "Confirm your email" heading — where a sign-in onto the email hold lands the keyboard. */
const EMAIL_HOLD_TITLE_ID = 'checkout-email-hold-title';
/** The calm "the bind waits for your email" line (identity#105) — focus moves on to it once it is shown. */
const BIND_HOLD_ID = 'checkout-bind-held-for-email';

/** The checkout's own "confirm your email" / "not your checkout" hold (orders S0.4) — its heading takes focus. */
const OWNERSHIP_HOLD_TITLE_ID = 'checkout-ownership-hold-title';

/*
 * `EMAIL_NOT_VERIFIED` (imported from `checkoutOwnership.ts`) is identity's
 * stable code for "this PATIENT has not confirmed their mailbox" —
 * `EmailVerifiedGuard`'s, and since identity#105 also `bind-assessment`'s FIRST
 * refusal — and, since orders S0.4, the checkout lanes' too. A code, not a
 * sentence: each service sends it as the whole of Nest's `message`, and
 * `api/client.ts` lifts that into `ApiError.detail` because these calls opt
 * into `parseError`. ONE constant for every service's version of it.
 */

/**
 * IS THIS BIND REFUSAL THE EMAIL HOLD (identity#105), rather than a failure?
 *
 * Exact status AND exact code. Every other bind refusal — the opaque 403
 * "Unable to link this assessment" (spent grant, email mismatch, staff), a 409,
 * a 5xx — is a real failure and keeps the generic line with its Try again. A
 * looser match (a regex on `detail`, or any 403) would dress one of those up as
 * "just confirm your email", and the patient would wait on a link that can
 * never add their order.
 *
 * A 403 never signs the patient out: only a 401 clears the session
 * (`api/client.ts`), so the same session is still there to re-bind with.
 */
function isBindHeldForEmail(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.detail === EMAIL_NOT_VERIFIED;
}

/**
 * What the card step says once a stale-window refusal has been answered by
 * re-reading the order (see `rereadCheckout`) — and it is said only when that
 * re-read comes back still awaiting payment, i.e. onto the screen the sentence
 * is about. `null` says nothing: an order already placed lands on the account
 * step or the confirmation, which say what happened on their own.
 *
 * A rotated SetupIntent gets the plan-changed sentence because a re-plan is the
 * only thing that rotates it (orders O-A): the card this window confirmed
 * belonged to a plan the case no longer holds.
 */
const STALE_REFUSAL_NOTICE: Record<StaleCheckoutRefusal, CheckoutNotice | null> = {
  plan_changed: { at: 'plan', message: CHECKOUT_PLAN_CHANGED_ELSEWHERE_MESSAGE },
  session_rotated: { at: 'plan', message: CHECKOUT_PLAN_CHANGED_ELSEWHERE_MESSAGE },
  safety_ack_required: { at: 'safety', message: CHECKOUT_SAFETY_ACK_REQUIRED_MESSAGE },
  already_placed: null,
};

/**
 * THERE IS NO 'confirm' STAGE ANY MORE (2026-08-20). The confirmation used to be
 * a component swap here, which meant checkout ended with the address bar exactly
 * where it started — nothing for a GTM History Change trigger to fire on and no
 * destination for a GA4 goal, on the one moment in the funnel marketing actually
 * needs. It is now a real route (`checkout/ThankYouPage.tsx`) and this file
 * NAVIGATES to it. Do not reintroduce a confirm stage: two confirmation surfaces
 * is how one of them silently stops being the one patients see.
 *
 * AND THERE IS NO SEPARATE 'address' / 'payment' STAGE ANY MORE (2026-09-23):
 * both live inside 'details', with the payment half revealed once an address is
 * saved. Old resume states map onto it — see the resume effect below.
 */
type Stage = 'account' | 'plan' | 'details';

/** Whose payment an attach's success is — see `finaliseWithSetupIntent`. */
type HandOver = 'placed_here' | 'revisit';

/**
 * THE STEP orders S0.4 REFUSED, so "I’ve confirmed it — continue" can send THE
 * SAME request again (2026-09-26; `checkoutOwnership.ts`). A description, never a
 * stored closure: the retry runs after the session has been re-read, from the
 * render that holds the fresh `me` — a closure captured at the refusal would
 * still believe the account unverified and send a now-verified patient to the
 * wrong screen after a successful attach (`accountReady`).
 *  - `plan`    — `checkout/selection`, re-sent with the same pick.
 *  - `session` — `checkout/session`, re-POSTed; and then whatever the press that
 *                asked for it was going to do next: nothing more (the safety
 *                tick's auto-prepare), the pay press (`startOrContinuePayment`),
 *                or the attach of a card Stripe already confirmed
 *                (`onSetupConfirmed`, same SetupIntent — never a second confirm).
 *  - `attach`  — the SAME SetupIntent re-attached, with the same hand-over.
 */
type HeldCheckoutStep =
  | { lane: 'plan'; next: PlanChoice }
  | { lane: 'session'; via: 'tick'; ack: SafetyAcknowledgement }
  | { lane: 'session'; via: 'pay' }
  | { lane: 'session'; via: 'confirmed'; setupIntentId: string; handOver: HandOver }
  | { lane: 'attach'; active: CheckoutSessionResult; setupIntentId: string; handOver: HandOver };

type CheckoutHold = {
  refusal: CheckoutOwnershipRefusal;
  step: HeldCheckoutStep;
  /**
   * orders refused AGAIN after the hold's continue — on the email hold the calm
   * "not yet" line, on the account-check screen its "still" line.
   */
  notYetConfirmed: boolean;
  /**
   * orders said `email_not_verified` while THIS ACCOUNT'S OWN SESSION said
   * verified (`me.emailVerified === true` in the render that sent the step) —
   * see `accountSaysVerified` in `holdOnOwnershipRefusal`. Nothing the patient
   * does with their mailbox can change that answer, so the hold drops the
   * "confirm your email" screen for the account-check one.
   */
  accountSaysVerified: boolean;
};

/**
 * Which hold SCREEN is up, or null for none. The view reset and the heading
 * focus key on it, so a switch between screens (the email hold → the account
 * check, or → "a different email address") lands at the top on the new heading,
 * while a "not yet" answer on the same screen keeps focus on the button that
 * asked.
 */
function holdScreenKey(hold: CheckoutHold | null): string | null {
  if (!hold) return null;
  return hold.accountSaysVerified ? `hold:${hold.refusal}:account` : `hold:${hold.refusal}`;
}

type PaymentBusy =
  | null
  | 'initialising'
  | 'finalising_stub'
  | 'finalising';

type AccountBootstrapState =
  | { status: 'loading' }
  | { status: 'ready'; email?: string; dateOfBirth?: string }
  | { status: 'error' };

type CheckoutRestoreState = 'idle' | 'loading' | 'ready' | 'error';

export function CheckoutFlow({
  assessmentId,
  clinicSlug,
  leadId,
  journeyEmail,
  journeyDob,
  startKg,
  bmi,
  reEntryToken,
  checkoutToken: checkoutTokenProp,
  onAssessmentGone,
  offerDropOffReason,
  paymentReturn: paymentReturnProp,
  onPaymentReturnSettled,
  preplanLoaderContent = false,
}: {
  assessmentId: string;
  clinicSlug: string;
  /** Step-0 lead — joins C5–C7 funnel events to the acquisition journey. */
  leadId?: string;
  /** Optional in-session hint — server account-bootstrap is authoritative. */
  journeyEmail?: string;
  /** Optional in-session hint — server account-bootstrap is authoritative. */
  journeyDob?: string;
  /**
   * The patient's recorded starting weight (kg) and clinic-calculated BMI, from
   * the assessment. Presentation only — they turn the plan builder's projected
   * percentages into this patient's own kilograms, and let the fit section state
   * the clinic's eligibility reasoning back to them. Both optional: a cold
   * resume may hold neither, and each is omitted rather than guessed.
   */
  startKg?: number;
  bmi?: number;
  /**
   * OTP-verified restore token from a return-visit ResultScreen code gate.
   * Consumed at register bind. Never the public checkout grant.
   */
  reEntryToken?: string;
  /** Public guest Orders grant; not identity. Falls back to sessionStorage. */
  /** T-2 — the plan-link lane only; passed straight to the plan step. */
  offerDropOffReason?: boolean;
  checkoutToken?: string;
  /**
   * Fired when the assessment-bound checkout session answers 404/409 — the
   * assessment is gone or not in a checkout-able state. Only the RESUME entry
   * (`/weightloss?resume=…`, P1E) passes this, to land on its "link expired"
   * fallback; in-journey checkouts keep the normal retryable error (TA-06).
   */
  onAssessmentGone?: () => void;
  /**
   * BACK FROM THE BANK'S 3D SECURE PAGE (2026-09-25): what Stripe's return
   * parameters say the bank decided, for THIS checkout — JourneyPage read them
   * before scrubbing them off the address bar (`setupRedirectReturn.ts`). Read
   * once, at mount; see `finishRedirectReturn`.
   */
  paymentReturn?: SetupRedirectResult;
  /** Called once that return has been acted on or set aside, so it is never handed over twice. */
  onPaymentReturnSettled?: () => void;
  /**
   * THE PRE-PLAN LANE (CTO, 2026-09-25 — `checkoutLoaderContent.ts`): JourneyPage
   * passes `true` ONLY when this checkout was opened from the Result screen of an
   * assessment this document just submitted — the self-serve lane, always the
   * hold route, no plan chosen yet. Only then does "Getting your order ready"
   * carry the checkout journey card (the icon-led card that grows as it builds)
   * and, when the restore lands on the plan chooser, stay up for
   * `CHECKOUT_PREPLAN_MIN_DWELL_MS` from its draw. Absent everywhere else: the
   * plain loader, released the moment the read lands.
   */
  preplanLoaderContent?: boolean;
}) {
  const { isSignedIn, status: authStatus, me, refresh, logout } = useAuth();
  const navigate = useNavigate();
  /** Where this checkout lives, basename-relative — the hold's Sign out comes back to it. */
  const location = useLocation();
  const accountReady = isSignedIn && me?.emailVerified === true;
  const [stage, setStage] = useState<Stage>('plan');
  /**
   * ORDERS S0.4 REFUSED A SIGNED-IN STEP (2026-09-26; `checkoutOwnership.ts`):
   * `email_not_verified` → "Confirm your email to continue", `checkout_not_owner`
   * → "This order was started with a different email address". While set, it IS
   * the screen (`CheckoutOwnershipHold`) — the refused step's controls are not
   * shown beside it, so nothing on the page invites a second press of a request
   * the server will refuse the same way. Cleared by a retry that is not refused
   * again, and by a re-read of the order.
   */
  const [checkoutHold, setCheckoutHold] = useState<CheckoutHold | null>(null);
  /** The hold's re-read + retry is running (its continue control is aria-disabled). */
  const [holdBusy, setHoldBusy] = useState(false);
  /**
   * WHILE A HOLD STANDS, THE ORDER IS NOT RE-READ UNDER IT. Confirming the
   * email flips `accountReady` — the hold's continue re-reads the session, and
   * `CheckEmailCard` re-reads it on every window focus — and that flip re-runs
   * the hard-refresh restore effect below (`accountReady` is one of its
   * dependencies). A re-read racing the hold's retry answers with the order as
   * it stood BEFORE the retry: `none` for a plan save that has not gone through
   * yet (back to the plan screen), and for a clinician-built plan, whose case
   * orders keeps `preapproved`, the plan summary screen — so a retry that
   * succeeded left the patient a step back. The retry IS the next request; a
   * stale-window refusal during it still re-reads, through `rereadCheckout`,
   * which clears the hold first. Written during render, so the effect sees the
   * hold as the render it runs after had it.
   */
  const holdStands = useRef(false);
  holdStands.current = checkoutHold !== null;
  /**
   * EVERY STAGE CHANGE LANDS AT THE TOP OF THE NEW SCREEN — the bug the CTO
   * reported: *"after payment it looks like it goes to a blank page… you have to
   * scroll to the top to get to the account creation section."* Payment is a
   * TALL screen (summary, trust strip, the safety gate, the card form); Create
   * account is a SHORT one. Swapping them inside one route leaves the window
   * scrolled past everything the new screen contains, so a patient who has just
   * handed over their card is looking at white space. Focus moves with the
   * scroll, because a screen-reader or keyboard user gets no signal at all from
   * a swap that never touched the URL — see ui/viewReset.ts.
   *
   * The ownership hold is a screen of its own too (2026-09-26): it replaces the
   * step without a stage change, and a retry that goes through replaces it again
   * — so the key carries it, and both swaps land at the top.
   */
  useResetViewOnChange(holdScreenKey(checkoutHold) ?? stage);
  /**
   * …and the keyboard lands on the hold's HEADING, not just the page region: the
   * control the patient pressed (Continue to delivery, the safety tick, Place
   * order) has left the page, and the heading says why in one line. Declared
   * after the view reset, so it runs after it. Only when the hold appears or its
   * kind changes — a "not yet" answer keeps focus on the button that asked.
   */
  const holdKind = holdScreenKey(checkoutHold);
  useEffect(() => {
    if (holdKind) document.getElementById(OWNERSHIP_HOLD_TITLE_ID)?.focus();
  }, [holdKind]);
  /**
   * `'register'` is the account step for a NEW patient and the only way in.
   * `'existing'` is reached ONLY by identity answering the register 409 — there
   * is no link to it (CTO 2026-09-24). `existingEmail` is the address that
   * register carried, locked on the sign-in view.
   */
  const [authMode, setAuthMode] = useState<'register' | 'existing'>('register');
  const [existingEmail, setExistingEmail] = useState<string | null>(null);
  const [choice, setChoice] = useState<PlanChoice | null>(null);
  const [accountBootstrap, setAccountBootstrap] = useState<AccountBootstrapState>({ status: 'loading' });
  const [bootstrapNonce, setBootstrapNonce] = useState(0);
  const retryAccountBootstrap = useCallback(() => setBootstrapNonce((n) => n + 1), []);
  const [checkoutToken, setCheckoutToken] = useState<string | undefined>(
    () => checkoutTokenProp ?? readCheckoutToken(assessmentId) ?? reEntryToken,
  );
  // Option C: only an OTP-verified restore token proves email. Seeded from a
  // return-visit code gate, never from the public checkout grant.
  const [verifiedToken, setVerifiedToken] = useState<string | undefined>(() => reEntryToken);
  /**
   * THE TOKENS A SIGN-IN MAY BIND WITH. Every grant this window has held
   * (oldest first), and the ones a register attempt has SPENT: identity redeems
   * the single-use re-entry token before it discovers the address already has
   * an account, so after a 409 the token that register carried is dead. See
   * `bindGrantToken`.
   */
  const grantsHeld = useRef<string[]>([]);
  const spentTokens = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (checkoutToken) writeCheckoutToken(assessmentId, checkoutToken);
    if (checkoutToken && !grantsHeld.current.includes(checkoutToken)) grantsHeld.current.push(checkoutToken);
  }, [assessmentId, checkoutToken]);
  const guestOpts = !isSignedIn && checkoutToken ? { checkoutToken } : undefined;
  // Form-first account step (in-flow only): we already hold the journey's email
  // and DOB, so the guest fills the register form FIRST (no code in sight), and
  // the email-code gate is revealed only when they press "Create account". A
  // cold return visit (resume link — DOB is gone) keeps the code-first order.
  const hasJourneyIdentity = Boolean(journeyEmail && journeyDob);
  // While true, the inline code gate is shown in place of the (still-mounted,
  // hidden) register form; `codeResolveRef` carries the promise the form's
  // submit is awaiting, resolved with the verified token when the code checks.
  const [awaitingCode, setAwaitingCode] = useState(false);
  const codeResolveRef = useRef<((token: string | null) => void) | null>(null);
  const resolveToken = useCallback(
    () =>
      new Promise<string | null>((resolve) => {
        codeResolveRef.current = resolve;
        setAwaitingCode(true);
      }),
    [],
  );
  const settleCode = useCallback((token: string | null) => {
    setAwaitingCode(false);
    const resolve = codeResolveRef.current;
    codeResolveRef.current = null;
    resolve?.(token);
  }, []);
  /**
   * WHERE THE KEYBOARD GOES WHEN THE FORM-FIRST CODE BOX LEAVES. The box, and
   * the button the patient just pressed, are removed from the page either way,
   * and a removed focused element drops focus to `<body>` — silence, at the
   * step's main transition. Written just before the box is settled and read by
   * the `awaitingCode` effect below: `'password'` after Back, `'confirmed'`
   * after a correct code.
   */
  const focusAfterCodeGate = useRef<'password' | 'confirmed' | null>(null);
  /** TRUE once THIS window's form-first code box accepted a code (renders EMAIL_CONFIRMED_STATUS). */
  const [emailConfirmedHere, setEmailConfirmedHere] = useState(false);
  const onCodeVerified = useCallback(
    (token: string) => {
      setVerifiedToken(token);
      setCheckoutToken(token);
      writeCheckoutToken(assessmentId, token);
      setEmailConfirmedHere(true);
      focusAfterCodeGate.current = 'confirmed';
      settleCode(token);
    },
    [settleCode, assessmentId],
  );
  /**
   * BACK OUT OF THE REVEALED CODE BOX to the password pair. Settling the pending
   * token as `null` is the form's own documented back-out: its submit stops
   * without registering and the form stays editable, with both boxes still
   * filled. The code already emailed stays valid — the gate is re-revealed with
   * `skipInitialSend`, so going back and forward never sends (and so never
   * retires) a code.
   */
  const backToPassword = useCallback(() => {
    focusAfterCodeGate.current = 'password';
    settleCode(null);
  }, [settleCode]);
  /**
   * identity says this address already has an account (register 409). Switch
   * the step to sign-in IN PLACE — same screen, address locked — and remember
   * the token that register spent so the sign-in's bind never tries it.
   */
  const onExistingAccount = useCallback((conflict: ExistingAccountConflict) => {
    if (conflict.spentToken) spentTokens.current.add(conflict.spentToken);
    setExistingEmail(conflict.email);
    setAuthMode('existing');
  }, []);
  const [checkoutRestore, setCheckoutRestore] = useState<CheckoutRestoreState>('loading');
  /**
   * TRUE only when the LATEST restore read landed on the plan CHOOSER: orders
   * answered `none` (nothing committed — not a malformed later state that fell
   * through to the plan) and no clinician-built plan exists, so the next screen
   * is "Choose your treatment". Set in the same batch as the
   * `ready` it describes, so the render in which the wait ends already knows
   * (the pre-plan hold is decided in that render — see `useLoaderHold`). Every
   * other landing (and every restore that starts) leaves it false, which is
   * what releases the loader at once.
   */
  const [restoreLandedOnChooser, setRestoreLandedOnChooser] = useState(false);
  /**
   * THE PLAN SCREEN'S READS, STARTED WHERE THE RESTORE LANDS ON IT (release
   * review, 2026-09-25 — `planScreenRead.ts`). Set in the same batch as
   * `restoreLandedOnChooser`, so the pre-plan hold's set loading time runs
   * ALONGSIDE the plan options and recommendation reads instead of in front of
   * them; `PlanSelection` then opens on the answer rather than reading again.
   * `state`: `failed` is an answer the plan screen shows as an error — the
   * loader lets go of it at once (REQUIREMENTS A8). Dropped at every restore
   * start and whenever the checkout leaves the plan stage, so a later visit to
   * the plan screen (an Edit plan round trip) reads afresh.
   */
  const [planRead, setPlanRead] = useState<{ read: EarlyPlanScreenRead; state: 'pending' | 'landed' | 'failed' } | null>(
    null,
  );
  /**
   * Once the checkout has shown the patient a real screen (anything but its
   * loader or the restore error), a later wait is a RE-READ — after a refusal,
   * a sign-in elsewhere — never "the loader that comes before selecting my
   * plan": it carries no card and is never held. Written by an effect after the
   * screen has painted, so the render that ENDS the entry wait still reads false.
   */
  const checkoutScreenShown = useRef(false);
  const [restoreNonce, setRestoreNonce] = useState(0);
  const retryCheckoutRestore = useCallback(() => setRestoreNonce((n) => n + 1), []);
  const [planPersistBusy, setPlanPersistBusy] = useState(false);
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  /**
   * TRUE when identity refused the sign-in bind because this account has not
   * confirmed its mailbox yet (identity#105, `isBindHeldForEmail`). NOT an error:
   * the bind is still owed (`bindOwed` is untouched by the refusal), the
   * "Confirm your email" screen says the order will be added once the link is
   * opened, and the email-hold effect re-binds and claims as soon as
   * `me.emailVerified` flips. Mutually exclusive with `claimError` — each attempt
   * clears both and sets at most one.
   */
  const [bindHeldForEmail, setBindHeldForEmail] = useState(false);
  /**
   * THE POST-PAYMENT CELEBRATION (CTO 2026-09-24): the modal a GUEST sees when a
   * fresh, successful attach moves them to the account step. Opened from ONE
   * place (the account branch of `finaliseWithSetupIntent`), so a refresh or a
   * resume onto the account step never replays it. See `checkout/Celebration.tsx`.
   */
  const celebration = usePostPaymentCelebration();

  /**
   * DID THIS DOCUMENT PLACE THE PAYMENT? Set in exactly ONE place: a successful
   * attach here (`finaliseWithSetupIntent`). NEVER set by the resume effect's
   * `complete` branch. `rereadCheckout` goes through that branch too, so a
   * re-read never sets it either.
   *
   * It decides `justCompleted` on the email-hold hand-over below, the one
   * hand-over that runs AFTER a resume or a re-read could have put this window
   * on the account step and that does not claim.
   *
   * THE CLAIM DOES NOT READ IT (journey UX review round 3, residual 1). A guest's
   * conversion is orders' answer to the claim, `claimed: true`, and nothing
   * else; see `finishAccountAndConfirm` for why this ref cannot stand in for it.
   *
   * WHY (journey UX review round 2, residual). A signed-in patient who has not
   * yet followed their email link cannot skip the account step. So when orders
   * answers a stale window's pay press with its "already" 409 (the OTHER window
   * paid), the re-read's `complete` branch lands on "Confirm your email", not on
   * a `justCompleted: false` confirmation. The email-hold hand-over then said
   * `justCompleted: true` once the link was followed, and the window that paid
   * nothing pushed `checkout_complete`. The paying window pushed its own too:
   * two conversions for one order. A hard refresh onto a finished order that is
   * waiting on the same link did the same.
   *
   * The rule ThankYouPage and the resume branch already state is that a window
   * resumed onto a finished order is not a conversion. That now holds on the
   * email-hold lane. It can undercount: a patient who paid, closed that tab and
   * verified from another tab is never counted. An undercount is the safe
   * direction here, because a duplicate looks like growth.
   *
   * A ref, not state: only hand-overs read it, never the render. It lives as long
   * as this document's checkout does, which is the claim it makes.
   */
  const paymentPlacedHere = useRef(false);

  /**
   * THE SETUPINTENT WHOSE CARD THIS DOCUMENT CONFIRMED (lost attach response,
   * journey UX review 2026-09-24). Set in exactly TWO places, both Stripe
   * answering this tab's own `confirmSetup` with a succeeded intent: in place
   * (`onCardConfirmedHere`), or — for a 3D Secure check that redirected — by the
   * bank sending the patient back to this page with that answer, verified with
   * Stripe before it counts (`finishRedirectReturn`, 2026-09-25). Stripe confirms
   * an intent once, so at most one window ever holds a given id.
   *
   * It is how `onSetupAlreadyConfirmed` tells this window's own retry from
   * another window's payment when Stripe says "this intent already succeeded".
   * Both tabs of one checkout hold the SAME session — orders keys the SetupIntent
   * on the case and its plan revision, not on the window — so "it is this
   * session's intent" cannot tell them apart. When the other tab confirmed the
   * card and its attach placed the hold, orders answers this tab's re-attach
   * with the same idempotent 200 'held' it gives a genuine retry. Counting that
   * answer here would push a second `add_payment_info` / `checkout_complete` for
   * one order (and, for a guest, a second celebration) — the duplicate the round
   * 2 and 3 fixes exist to prevent.
   *
   * Memory only, so a reload forgets it. That window's re-attach then hands over
   * as a revisit and counts nothing: the accepted undercount, never a duplicate
   * (see `paymentPlacedHere`).
   */
  const setupConfirmedHere = useRef<string | null>(null);

  /**
   * THE 3D SECURE RETURN THIS DOCUMENT OWES AN ANSWER (2026-09-25). Non-null
   * from mount until `finishRedirectReturn` has acted on it or set it aside;
   * while it is non-null and the card step is what the resume restored, the
   * page shows "Finishing your order" instead of a card form the patient has
   * already filled in (and a safety tick that is not restored). See
   * `setupRedirectReturn.ts` for the defect and the return address.
   */
  const [redirectReturn, setRedirectReturn] = useState<SetupRedirectResult | null>(() => paymentReturnProp ?? null);
  /** The return is acted on ONCE — a re-render, a re-read or a StrictMode re-run must not attach twice. */
  const redirectReturnTaken = useRef(false);
  /**
   * TRUE once this document is finishing a payment the patient confirmed on
   * their bank's page. A full page load forgets the journey's opaque `leadId`
   * (it lives in memory only, and must never ride a URL or device storage), so
   * the one `add_payment_info` this paying window owes is pushed WITHOUT
   * `lead_id` (`finaliseWithSetupIntent`), the way `checkout_complete` already
   * is when the id is gone. It still carries `order_id`, the join key to the
   * server-side purchase.
   */
  const returnedFromBank = useRef(false);

  /**
   * CHECKOUT IS OVER — leave for the confirmation ROUTE.
   *
   * ONE EXIT, so every path that finishes a checkout hands over the same shape
   * and lands on the same page: pay-as-a-verified-patient, register-then-claim,
   * sign-in-then-claim, the email-verified hold, and a hard refresh that resumes
   * onto an OrderCase that is already complete.
   *
   * `replace`, not push, and that is a patient-safety choice rather than a
   * tidiness one: after the order is placed, Back must NEVER return to a live pay
   * control for the order they just placed. Replacing the journey entry means
   * Back leaves the way they came in instead of walking them onto a payment
   * screen for a treatment they have already bought.
   *
   * THE CAPTURE ROUTE IS PASSED IN, never read off `confirmRoute` here: the call
   * that has just finalised payment holds the server's fresh `paymentPhase`,
   * while this closure still holds the render-time value from before the response
   * landed. Reading the stale one would tell a Route 2 patient their card had not
   * been charged when it had.
   */
  function goToConfirmation(next: {
    captureRoute: PaymentCaptureRoute;
    medicationName?: string;
    /**
     * False when we are re-showing a confirmation for an order finished earlier,
     * or finished by another window (see `paymentPlacedHere`).
     */
    justCompleted: boolean;
  }) {
    const state: ThankYouNavState = {
      orderPlaced: true,
      justCompleted: next.justCompleted,
      // The guest already had the celebration on the account step; the
      // confirmation must not throw a second burst of confetti.
      celebrated: celebration.wasShown(),
      captureRoute: next.captureRoute,
      medicationName: next.medicationName,
      leadId,
    };
    // The state rides in the HISTORY ENTRY. Nothing identifying may go in the URL
    // — see checkout/thankYouRoute.ts for why a query string here would put an
    // order (and a health inference about a named person) into every tag in a
    // container administered outside this estate.
    // The confirmation's confetti plays only for a hand-over made in THIS
    // document — a reload of /thank-you keeps `justCompleted` but not this.
    if (next.justCompleted) noteCheckoutHandOver();
    navigate(THANK_YOU_PATH, { replace: true, state });
  }

  /**
   * A grant the sign-in bind can still use — never one a register spent.
   *
   * Newest first: the live `checkoutToken`, then every earlier grant this window
   * held (on the form-first lane the verified code's token REPLACED the checkout
   * grant, and that is exactly the token a 409'd register burned, so the grant
   * from before the code is the one that still works). When every grant held
   * was spent — the lane where the only token was the upstream restore token —
   * mint a fresh one through the journey's own public guest-lane mint. That
   * mint is no new capability (it is public, keyed by the assessment id); the
   * grant only names which assessment, and identity then requires the SIGNED-IN
   * account's stored email to match the assessment's. ⚠️ That stored email is
   * not required to be VERIFIED until identity#105 ships — see the file header;
   * a server-side gap, not one this function can close. Nothing held and
   * nothing spent → no bind, as before.
   */
  async function bindGrantToken(): Promise<string | undefined> {
    const spent = spentTokens.current;
    const live = [checkoutToken, ...[...grantsHeld.current].reverse()].find((t) => t && !spent.has(t));
    if (live) return live;
    if (spent.size === 0) return undefined;
    const { checkoutToken: minted } = await mintCheckoutToken(assessmentId, clinicSlug);
    grantsHeld.current.push(minted);
    return minted;
  }

  /**
   * TRUE from a sign-in's first attempt until its bind succeeds. The claim
   * alert's "Try again" calls `finishAccountAndConfirm()` with no options, and
   * before this ref a sign-in whose BIND had failed retried only the claim —
   * which orders refuses for an unbound assessment, forever. Now a retry re-binds
   * while a bind is still owed, and never re-binds once one has landed.
   */
  const bindOwed = useRef(false);

  async function finishAccountAndConfirm(opts?: { bindGrant?: boolean }) {
    if (opts?.bindGrant) bindOwed.current = true;
    setClaimBusy(true);
    setClaimError(null);
    setBindHeldForEmail(false);
    // True only while `bindAssessment` itself is in flight, so the email-hold
    // reading below applies to identity's answer and never to the grant mint
    // (clinic) or the claim (orders).
    let binding = false;
    try {
      // Login-after-pay: bind WHO (email match) before claim. Register already
      // bound during identity register. A public-minted token is never identity.
      if (bindOwed.current) {
        const grant = await bindGrantToken();
        if (grant) {
          binding = true;
          await bindAssessment({ reEntryToken: grant });
          binding = false;
        }
        bindOwed.current = false;
      }
      const { claimed } = await claimCheckout({ assessmentId });
      clearCheckoutToken(assessmentId);
      goToConfirmation({
        captureRoute: confirmRoute,
        medicationName: choice?.medicationName,
        // A GUEST'S CONVERSION IS THE CLAIM THAT MOVED THE ORDER, AND ONLY THAT
        // (journey UX review round 3, residual 1). orders' `claimCheckout`
        // moves the case from the guest principal onto the account once, and
        // answers `claimed: true` for that call alone. When the case is already
        // on this account it answers `claimed: false`, with success.
        //
        // Nothing before the claim puts the case there: identity's bind stamps
        // the ASSESSMENT's owner in clinic-service, never the OrderCase, and
        // orders reads the case by the guest principal until this call moves it.
        // So a first claim answers `true`. `false` means another claim moved it
        // first: another tab, or this tab's own earlier claim whose answer was
        // lost on the way back.
        //
        // Keyed on anything else, one order counts twice. Tab A pays; tab B, stale,
        // is refused with the "already" 409 and re-reads onto this account step.
        // - Setting `paymentPlacedHere` on any successful claim (the round-2 code)
        //   let B's `claimed: false` count after A's claim had counted.
        // - Reading `paymentPlacedHere` here at all lets A count on `false` when B
        //   signs in and claims first, because A's attach set it.
        // orders' one move of the case is the fact both tabs share. Two claims
        // racing inside the same instant cannot both be told `true`: orders'
        // claim is a compare-and-swap on the guest principal (updateMany on the
        // case id AND the guest owner, claimed = count === 1; orders
        // feat/checkout-replan-awaiting-payment, 2026-09-25), so exactly one
        // racing claim answers `claimed: true`.
        //
        // The cost is an undercount: a lost claim response retried answers
        // `false`, so that order is not counted anywhere. An undercount is the safe
        // direction; a duplicate looks like growth. `=== true`, so an older orders
        // build that sent no flag counts nothing, the same direction.
        //
        // The email hold's retry after a failed claim comes through here too, so
        // it is keyed the same way.
        justCompleted: claimed === true,
      });
    } catch (err) {
      // THE EMAIL HOLD, NOT A FAILURE (identity#105). The account is real and the
      // grant may be fine; the mailbox is simply unconfirmed. The throw landed
      // before `bindOwed.current = false`, so the bind is still owed and the
      // email-hold effect re-binds once `me.emailVerified` flips. The red line's
      // "Try again" could only earn the same 403 until then.
      if (binding && isBindHeldForEmail(err)) {
        setBindHeldForEmail(true);
        return;
      }
      const mapped = mapCheckoutApiError(err, 'Sorry — we could not link your order to this account. Please try again.');
      setClaimError(mapped.message);
    } finally {
      setClaimBusy(false);
    }
  }

  // Guest Create-account: fetch authoritative email/DOB from the assessment.
  // Hard refresh on checkout still works because JourneyPage restores via
  // ?assessment= → result → See my plan, and this effect re-runs on mount.
  useEffect(() => {
    // Wait for the verified token — until the code gate mints one there is no
    // authoritative identity to read (and no way to bind the account). The
    // token rides in the POST body, so the assessment id never hits a URL/log.
    // Skip entirely in-flow: we already hold the journey email + DOB, so the
    // form-first path prefills from those and never needs this bootstrap read.
    if (stage !== 'account' || authMode !== 'register' || !verifiedToken || hasJourneyIdentity) return;
    let cancelled = false;
    setAccountBootstrap({ status: 'loading' });
    getAssessmentAccountBootstrap(verifiedToken)
      .then((boot) => {
        if (cancelled) return;
        setAccountBootstrap({
          status: 'ready',
          email: boot.email ?? journeyEmail,
          dateOfBirth: boot.dateOfBirth ?? journeyDob,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 404 on resume entry — expired link. Otherwise Retry (keep assessment).
        if (err instanceof ApiError && err.status === 404 && onAssessmentGone) {
          onAssessmentGone();
          return;
        }
        setAccountBootstrap({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [stage, authMode, verifiedToken, journeyEmail, journeyDob, hasJourneyIdentity, bootstrapNonce, onAssessmentGone]);

  // Form-first pre-send: for the in-flow account step, email the 6-digit code
  // silently on arrival (before the visitor even reaches "Create account") so
  // it's waiting when the gate reveals. Once only — RestoreCodeEntry then mounts
  // with skipInitialSend so it doesn't re-send and invalidate this code. Never
  // fires once a token is already in hand (upstream restore) or after sign-in.
  const preSentCode = useRef(false);
  useEffect(() => {
    if (stage !== 'account' || authMode !== 'register' || isSignedIn) return;
    if (!hasJourneyIdentity || verifiedToken) return;
    if (preSentCode.current) return;
    preSentCode.current = true;
    void requestRestoreCode(assessmentId).catch(() => undefined);
  }, [stage, authMode, hasJourneyIdentity, verifiedToken, assessmentId, isSignedIn]);

  // The switch to sign-in replaces the form the patient was pressing "Create
  // account" on, so the keyboard would otherwise fall to <body>. It lands on
  // the password — the one thing left to type — whose `aria-describedby` reads
  // out WHY the screen changed (a live region inserted with its text already
  // in it is not reliably announced; a focused field's description is).
  useEffect(() => {
    if (stage === 'account' && authMode === 'existing') document.getElementById(LOGIN_PASSWORD_ID)?.focus();
  }, [stage, authMode]);

  // THE FORM-FIRST CODE BOX TAKES THE KEYBOARD WHEN IT APPEARS, AND HANDS IT ON
  // WHEN IT GOES (review, 2026-09-24; WCAG 2.4.3 / 4.1.3). "Create account" hides
  // the form holding the button that was just pressed, so without this the focus
  // fell to <body> and the heading, the code box and the spam/junk help were all
  // silent. The code box's `aria-describedby` already names that help, so landing
  // on the box reads it. On the way out: Back returns to the first password box
  // (both still filled); a correct code lands on the "your email is confirmed"
  // line, which stays true whatever register answers next.
  useEffect(() => {
    if (awaitingCode) {
      document.getElementById('restore-code')?.focus();
      return;
    }
    const target = focusAfterCodeGate.current;
    focusAfterCodeGate.current = null;
    if (target === 'password') document.getElementById(REGISTER_PASSWORD_ID)?.focus();
    else if (target === 'confirmed') document.getElementById(EMAIL_CONFIRMED_ID)?.focus();
  }, [awaitingCode]);

  /** Signed in to an account that has not confirmed its mailbox: the "Confirm your email" screen. */
  const onEmailHold = Boolean(isSignedIn && me && !me.emailVerified);
  /**
   * TRUE from a sign-in on this page's sign-in view until the bind and claim it
   * started have settled. Set in the LoginForm's `onSuccess`, never by a resume.
   */
  const signInFocusPending = useRef(false);
  // A SIGN-IN ONTO THE EMAIL HOLD HANDS THE KEYBOARD ON (review, 2026-09-25;
  // WCAG 2.4.3 / 4.1.3). Signing in to an UNCONFIRMED account swaps the sign-in
  // view for "Confirm your email" without a stage change, so
  // `useResetViewOnChange` does not run, and the password box and Sign in button
  // that had focus are gone: focus falls to <body>, and the calm hold line
  // (identity#105) arrives as a status region with its text already in it, which
  // is not reliably announced. So: while the bind and claim run, focus lands on
  // the screen's heading; once they settle on the hold, it moves on to the hold
  // line itself, which a screen reader then reads. Only from <body> or our own
  // heading — never out of a control the patient has moved to themselves.
  useEffect(() => {
    if (!signInFocusPending.current || stage !== 'account') return;
    const settled = !claimBusy;
    if (settled) signInFocusPending.current = false;
    if (!onEmailHold) return;
    const active = document.activeElement;
    const focusIsLost = !active || active === document.body || active.id === EMAIL_HOLD_TITLE_ID;
    if (!focusIsLost) return;
    document.getElementById(settled && bindHeldForEmail ? BIND_HOLD_ID : EMAIL_HOLD_TITLE_ID)?.focus();
  }, [stage, onEmailHold, claimBusy, bindHeldForEmail]);

  // BACK FROM THE FORGOTTEN-PASSWORD TAB, THE KEYBOARD IS ON THE PASSWORD. The
  // link opens the reset in a new tab, and the only sign-in that links this paid
  // order is the one on this page — so when the patient returns here after using
  // it, land them on the box they came back to fill. Once per use of the link,
  // and only on this view.
  const forgotLinkUsed = useRef(false);
  useEffect(() => {
    if (stage !== 'account' || authMode !== 'existing') return;
    const onReturn = () => {
      if (!forgotLinkUsed.current || document.visibilityState === 'hidden') return;
      forgotLinkUsed.current = false;
      document.getElementById(LOGIN_PASSWORD_ID)?.focus();
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [stage, authMode]);

  // Signed-in but unverified: they paid on Bearer, then wait on the 24h email
  // link. Do not auto-advance a guest who just registered — that path still
  // has to claim the OrderCase.
  const heldForEmailLink = useRef(false);
  useEffect(() => {
    if (stage === 'account' && isSignedIn && me && me.emailVerified === false) {
      heldForEmailLink.current = true;
    }
  }, [stage, isSignedIn, me]);
  useEffect(() => {
    if (stage !== 'account' || !heldForEmailLink.current || !me?.emailVerified) return;
    heldForEmailLink.current = false;
    // A FAILED CLAIM IS STILL OWED. Confirming the mailbox does not put the paid
    // order on the account — that is what `claimCheckout` does, and this patient
    // reached the email hold by signing in to an existing unverified account
    // AFTER paying as a guest. Advancing here would show a confirmation screen
    // for an order that is not on their account.
    //
    // Retry it ONCE, here, rather than advancing: the common cause of the first
    // failure is the account not yet being verified, which is precisely what has
    // just changed. `heldForEmailLink` is already cleared, so a second failure
    // cannot loop — it lands on the visible error with its own Try again.
    //
    // A BIND HELD FOR THE EMAIL (identity#105) IS THE SAME DEBT, SAID CALMLY. The
    // bind is still owed, so this one retry re-binds (identity reads the flag
    // live, same session) and then claims. Without `bindHeldForEmail` here the
    // hold would advance to a confirmation for an order that is on nobody's
    // account.
    if (claimError || bindHeldForEmail) {
      void finishAccountAndConfirm();
      return;
    }
    // A CONVERSION ONLY IF THIS WINDOW PAID. The account step is reached two
    // ways: this window's own attach, or a resume / re-read onto an order that
    // was already `complete`. That includes the stale window whose pay press
    // orders refused because another window had paid. Only the first way is a
    // conversion. See `paymentPlacedHere`.
    goToConfirmation({
      captureRoute: confirmRoute,
      medicationName: choice?.medicationName,
      justCompleted: paymentPlacedHere.current,
    });
    // `goToConfirmation` closes over this render's `confirmRoute`/`choice`/`leadId`,
    // all of which are set by the time an order can reach the email hold; adding it
    // as a dependency would re-run this effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, me?.emailVerified, claimError, bindHeldForEmail]);
  // A clinician-built plan pre-approved for this assessment (2026-07-20): when
  // set, plan-selection is replaced by a read-only summary and paying captures
  // immediately. null = none (the normal patient-picks-a-plan flow).
  const [clinicianPlan, setClinicianPlan] = useState<PlanChoice | null>(null);
  const [address, setAddress] = useState<Address | null>(null);
  /**
   * TRUE while the address form is re-opened over a saved address (Change).
   * While it is, the payment half of the merged step is WITHDRAWN — absent,
   * not disabled — and no checkout session may be minted: `POST
   * checkout/session` is the only call that writes `deliveryAddress` onto the
   * OrderCase, so a pay control beside an open address form would let a
   * patient pay while looking at an address the order may not carry. It is
   * keyed on the form being OPEN, not on it being dirty: a clean open form is
   * one keystroke from dirty, and that keystroke must not be able to land
   * while a payment is running (the review finding this replaces).
   */
  const [editingAddress, setEditingAddress] = useState(false);
  /**
   * TRUE while the re-opened form's fields differ from the saved address
   * (AddressForm.onDirtyChange). It only chooses the words of the status line
   * under the open form — the withdrawal itself is `editingAddress`.
   */
  const [addressDirty, setAddressDirty] = useState(false);
  /**
   * TRUE while Stripe's `confirmSetup` (and the attach that follows it) is in
   * flight — lifted out of `PaymentElementsForm`, where it used to be private.
   * `placing` alone does not cover that window: `busy` only rises once the
   * confirm hands the SetupIntent back. Together they lock Change.
   */
  const [confirming, setConfirming] = useState(false);
  /**
   * TRUE when orders has FIXED the monthly this card step must print, and
   * `choice.monthlyPence` is that figure: the checkout was restored onto an
   * `awaiting_payment` session (the resume echoes the monthly stored at the FIRST
   * session mint, and orders does NOT re-quote such a case), or an Edit plan round
   * trip came back on a case this checkout had already minted (see `caseMinted`).
   * The card step then prints orders' figure rather than the live ladder. See
   * `checkout/cardStepPrice.ts`.
   */
  const [ordersPriceFixed, setOrdersPriceFixed] = useState(false);
  /**
   * STICKY: orders has an `awaiting_payment` case for this checkout, so its
   * monthly is STORED and will not be re-quoted by the next session POST — set
   * when `prepareSession` succeeds and when the resume restores an
   * `awaiting_payment` session, and never cleared (a case never goes back to a
   * draft).
   *
   * WHY IT EXISTS (review finding, 2026-09-24). Only the resume used to mark the
   * price as fixed, so on the in-flow lane — tick, session minted at £X, the
   * catalogue repriced to £Y, Edit plan and back with the same plan — the rail
   * re-read the live ladder and said "Held on your card today £Y … the price has
   * changed", while orders held £X. After an Edit plan round trip on a minted
   * case the selection echo IS what orders will hold: the stored first-mint
   * monthly when nothing changed, the re-quoted-and-stored one after a re-plan.
   * A ref, because only an event handler reads it (`continueToAddress`), and
   * never the render: before an Edit plan the in-flow `choice.monthlyPence` is
   * the SELECTION-time quote, not the mint-time price.
   */
  const caseMinted = useRef(false);
  /**
   * Does the session in state carry THIS window's safety acknowledgement? True
   * once `prepareSession` has POSTed one (orders recorded it); false for a
   * session handed back by `checkout/resume`, which records nothing.
   *
   * WHY (review findings, 2026-09-24). A product re-plan clears the case's
   * acknowledgement, and the resume lane used to reuse the resumed session for
   * the pay press — so a patient who refreshed after Edit plan ticked in the
   * browser only, and the order reached the clinician with no acknowledgement on
   * it. Now the tick on a resumed session POSTs `checkout/session` once (orders
   * reuses the same case and records the ack), and both pay paths insist on it
   * before the attach. orders refuses a hold without an acknowledgement too
   * (O-C); this is the half that keeps an honest patient from meeting that
   * refusal.
   */
  const sessionCarriesAck = useRef(false);
  /** Where focus goes after the address form opens or collapses (see the effect below). */
  const pendingAddressFocus = useRef<'editor' | 'saved' | null>(null);
  const [prefill, setPrefill] = useState<Partial<Address> | undefined>();
  const [session, setSession] = useState<CheckoutSessionResult | null>(null);
  const [attachResult, setAttachResult] = useState<AttachPaymentMethodResult | null>(null);
  const [busy, setBusy] = useState<PaymentBusy>(null);
  const [payError, setPayError] = useState<string | null>(null);
  /** Validated publishable key — set only when mounting real Elements (never invented). */
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  /**
   * STICKY FOR THE REST OF THIS CHECKOUT: the card has been confirmed — Stripe's
   * `confirmSetup` returned SUCCEEDED (`onSetupConfirmed`), or the stub lane took
   * its stand-in (`startOrContinuePayment`) — and it is set BEFORE the attach
   * that follows. From here the SetupIntent carries a card, and orders will
   * refuse to re-plan the case (a succeeded SetupIntent / a `month1_preauth`
   * record), so Edit plan is withdrawn and never comes back — including after a
   * FAILED attach, when `busy`, `confirming` and `attachResult` all read as if
   * nothing had happened. Recovery from there is a hard refresh, which reads
   * `checkout/resume` — or `rereadCheckout`, which is the same read in-flow — and
   * both clear it: after them the screen is the server's current order, and
   * orders (not this flag) refuses a re-plan whose card setup has begun (R41).
   * The one other thing that clears it is `offerDifferentCard` (2026-09-25):
   * after a DECLINED hold orders rotates the case onto a NEW SetupIntent that
   * carries no card, so the card setup really has not begun on it — orders
   * allows a re-plan again, and Edit plan comes back with it.
   */
  const [cardSetupConfirmed, setCardSetupConfirmed] = useState(false);
  /**
   * THE LAST ATTACH NEVER ANSWERED (`attachOutcomeUnknown` → `payOutcomeUnknown`),
   * so the month-1 hold may already stand on the address this window sent
   * (review, 2026-09-25). While this is true, Change on the delivery address is
   * LOCKED, with the same visible reason as during a payment attempt, because
   * the attempt has not finished: the patient is told to press Place order again
   * to check. Without the lock, Change + a new address + the tick POSTed a
   * session that orders refuses once money is committed ("already", #245); the
   * re-read then landed on "Order received" with the OLD address on the order,
   * no word that the new one was not saved, and this window's own payment
   * counted nowhere.
   *
   * Cleared the moment an attach is sent again (the next press, which is the
   * check), so a known answer settles it: a success moves the patient on, a stale
   * refusal re-reads (`rereadCheckout` clears it too), a decline or any other
   * refusal is a KNOWN outcome with nothing held and Change comes back. Set again
   * only by another unknown answer.
   */
  const [attachOutcomePending, setAttachOutcomePending] = useState(false);
  /**
   * Edit plan (CTO 2026-09-24): the plan the patient left the Delivery & payment
   * step to change, while that round trip is open — PlanSelection opens on it,
   * and `continueToAddress` compares the server's echo against it. Null
   * otherwise.
   */
  const [planEditFrom, setPlanEditFrom] = useState<InitialPlanChoice | null>(null);
  /**
   * What the card step owes the patient after the server answered their own
   * action differently from what they asked — a refused plan change, or a pay
   * attempt refused because the order moved on in another window. Never a
   * silent snap-back. See `CheckoutNotice` for where each one sits.
   */
  const [checkoutNotice, setCheckoutNotice] = useState<CheckoutNotice | null>(null);
  /**
   * The notice a refusal is WAITING to show: set when `rereadCheckout` starts,
   * shown only when the re-read lands on the screen it is about (see the resume
   * effect), so a sentence about "your order below" never sits over a loader or
   * over the previous window's plan.
   */
  const pendingRefusalNotice = useRef<CheckoutNotice | null>(null);
  /**
   * Move focus to the notice once it has painted — see `CHECKOUT_NOTICE_ID` for
   * why a live region alone is not heard here.
   *
   * ⚠️ THIS EFFECT MUST STAY BELOW `useResetViewOnChange(stage)`. A refused Edit
   * plan changes the stage in the same commit that paints the notice, and the
   * reset then moves focus to the top of the page. Effects in one component run
   * in the order they are declared (and after every child's), so declaring this
   * one later is what makes it run after the reset and win. It has no dependency
   * list on purpose: the notice can paint a commit or two after it is set (a
   * re-read shows a loader first), and the flag makes every other run a no-op.
   */
  const noticeFocusPending = useRef(false);
  useEffect(() => {
    if (!noticeFocusPending.current || !checkoutNotice) return;
    const target = document.getElementById(CHECKOUT_NOTICE_ID);
    if (!target) return;
    noticeFocusPending.current = false;
    target.focus();
  });
  // Payment-step copy from plan-preview only — never query params.
  const previewRoute = paymentCaptureRoute(clinicianPlan !== null);
  const payCopy = paymentStepCopy(previewRoute);
  // Confirmation route is server-authoritative once finalisation returns.
  const confirmRoute: PaymentCaptureRoute = attachResult
    ? paymentPhaseToCaptureRoute(attachResult.paymentPhase)
    : previewRoute;

  /**
   * THE MEDICATION-SAFETY CONTENT for the chosen treatment, read at the payment
   * stage (clinical feedback, Reneal Patel, 2026-08-18).
   *
   * WHY IT IS READ HERE AND NOT THREADED DOWN FROM `PlanSelection`. Two of the
   * three routes onto this screen never touch the chooser: the clinician-led
   * lane shows a pre-built plan and skips it entirely, and a hard-refresh resume
   * lands straight on Payment with the plan restored from the OrderCase. A prop
   * carried from the chooser would be `undefined` on exactly those paths — and
   * absent content means "we cannot show you the risks", so it would block
   * checkout for a resuming patient who had done nothing wrong. This read
   * answers on every path, and it is the same authorised plan-options call the
   * chooser already makes (guest grant or PATIENT session), so it opens no new
   * surface.
   */
  const [safetyView, setSafetyView] = useState<MedicationSafetyView>({ state: 'loading' });
  const [safetyNonce, setSafetyNonce] = useState(0);
  const retrySafety = useCallback(() => setSafetyNonce((n) => n + 1), []);
  /**
   * The chosen option's per-term price ladder, kept from the SAME plan-options
   * read the safety effect below already makes — no extra request. It is the
   * card step's price source on the hold route (prices-service is what orders
   * re-quotes from at session mint) and the source of every term total, through
   * the SAME `termPricing` the plan step renders. See `cardPrice`.
   *
   * WHY RELYING ON IT COSTS NOTHING: the safety gate blocks the pay control
   * whenever this read has not answered ("no content ⇒ no sale"), so whenever a
   * patient can pay, the ladder for their product is in hand.
   */
  const [catalogPlans, setCatalogPlans] = useState<PricePlan[] | null>(null);
  /**
   * The tick. DELIBERATELY not persisted and not restored on a hard refresh —
   * the same posture as the commitment tick in `CommitmentTerms`. This is a
   * statement the patient makes at the moment they pay; reinstating it from
   * storage would be the browser making it on their behalf.
   */
  const [safetyAccepted, setSafetyAccepted] = useState(false);

  const placing = busy !== null;
  const elementsReady =
    session !== null &&
    decidePaymentSessionFlow(session.paymentSession).kind === 'elements' &&
    Boolean(publishableKey);

  function applyPublishableKeyForSession(active: CheckoutSessionResult): string | null {
    const decision = decidePaymentSessionFlow(active.paymentSession);
    if (decision.kind !== 'elements') {
      setPublishableKey(null);
      return null;
    }
    try {
      const key = requireStripePublishableKey();
      setPublishableKey(key);
      return key;
    } catch {
      setPublishableKey(null);
      setPayError(PAYMENT_UNAVAILABLE_MESSAGE);
      return null;
    }
  }

  // Once signed in, best-effort prefill the delivery address from the profile.
  useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;
    getPatientProfile()
      .then((profile) => {
        if (!cancelled && profile.registeredAddress) setPrefill(profile.registeredAddress);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isSignedIn]);

  // Hard-refresh restore: OrderCase is authoritative for Address / Payment /
  // account (post-pay guest) / confirm. Soft status:'none' → plan (+ clinician
  // plan-preview). Guests need a checkoutToken; signed-in uses Bearer.
  // Once this session has attached payment (`attachResult`), do NOT re-run
  // resume on auth flip (register/login) — that would rewind to Plan because
  // the OrderCase is still guest-owned until claim finishes.
  useEffect(() => {
    if (authStatus === 'loading') return;
    // Not under an ownership hold — see `holdStands`. The hold's own retry is
    // the next request; a re-read racing it would land on the order as it was
    // BEFORE that retry and pull the patient back a step.
    if (holdStands.current) return;
    // Only the chooser branch below sets it true; every other way out of this
    // effect — including the signed-out lane that lands on the plan WITHOUT a
    // read — is not the pre-plan hold (checkoutLoaderContent.ts). Belt and
    // braces: a later restore is also shut out by `checkoutScreenShown`, so no
    // spec fails without this reset — it keeps the flag describing THIS read.
    setRestoreLandedOnChooser(false);
    setPlanRead(null);
    if (attachResult) {
      setCheckoutRestore('ready');
      return;
    }
    if (!isSignedIn && !checkoutToken) {
      setCheckoutRestore('ready');
      setStage('plan');
      return;
    }
    let cancelled = false;
    setCheckoutRestore('loading');
    const opts = !isSignedIn && checkoutToken ? { checkoutToken } : undefined;
    getCheckoutResume(assessmentId, opts)
      .then(async (resume) => {
        if (cancelled) return;
        // A refusal's notice (see `rereadCheckout`) is owed ONLY to the screen it
        // talks about — an order still awaiting payment. Every landing takes it
        // off the queue; only that one shows it.
        const refusalNotice = pendingRefusalNotice.current;
        pendingRefusalNotice.current = null;
        // A CLINICIAN-BUILT PLAN THIS PATIENT HAS ALREADY BOUND (returning
        // patients S7.2b, 2026-09-25; orders S7.2a). The callback lane's plan
        // link keeps its case `preapproved` until the attach, so the card step
        // it left for the bank's 3D Secure page (or a hard refresh of that step)
        // used to come back to `none` and was set aside on the plan screen.
        // orders now answers `clinician_plan` — ONLY to the account the case is
        // bound to, never to a guest principal — with the clinician's STORED
        // plan and monthly, the stored address and the case's OWN card session
        // (the session POST's SetupIntent, never a second one). It lands on the
        // card step exactly as an `awaiting_payment` resume does, with
        // `clinicianPlan` set, so the screen is the clinician-led one: the
        // captured route's copy, orders' fixed figure ("Paid today"), no Edit
        // plan. `finishRedirectReturn` then finishes a bank return through the
        // attach unchanged (Route 2 captures).
        //
        // Every field is required. A `clinician_plan` missing any of them — or an
        // orders build without S7.2a, which answers `none` for the same case —
        // falls through to the plan-preview path below: the screen this page
        // showed before the status existed. `none` is also what a guest gets.
        if (
          resume.status === 'clinician_plan' &&
          resume.paymentSession &&
          resume.orderCaseId &&
          resume.subscriptionId &&
          resume.productKey &&
          resume.months &&
          resume.deliveryAddress
        ) {
          const clinicianChoice: PlanChoice = {
            productKey: resume.productKey,
            months: resume.months,
            medicationName: resume.medicationName ?? 'Your treatment',
            monthlyPence: resume.monthlyPence ?? 0,
          };
          setClinicianPlan(clinicianChoice);
          setChoice(clinicianChoice);
          setAddress(resume.deliveryAddress);
          setPrefill(resume.deliveryAddress);
          const active: CheckoutSessionResult = {
            orderCaseId: resume.orderCaseId,
            subscriptionId: resume.subscriptionId,
            paymentSession: resume.paymentSession,
          };
          setSession(active);
          // The same bookkeeping as the `awaiting_payment` restore below: the
          // case exists and its monthly is FIXED (Route 2 captures the stored
          // figure — `clinicianPlan` already makes the card step print it), and
          // this session carries no acknowledgement from THIS window, so the
          // tick re-POSTs `checkout/session` (orders re-binds the same patient
          // and records it). A 3D Secure return sets it back in
          // `finishRedirectReturn`: the tick went with the card, before the bank.
          caseMinted.current = true;
          sessionCarriesAck.current = false;
          setOrdersPriceFixed(true);
          applyPublishableKeyForSession(active);
          setStage('details');
          if (refusalNotice) {
            setCheckoutNotice(refusalNotice);
            noticeFocusPending.current = true;
          }
          setCheckoutRestore('ready');
          return;
        }
        if (resume.status === 'awaiting_payment' && resume.paymentSession && resume.orderCaseId && resume.subscriptionId) {
          const restoredChoice: PlanChoice = {
            productKey: resume.productKey ?? '',
            months: resume.months ?? 1,
            medicationName: resume.medicationName ?? 'Your treatment',
            monthlyPence: resume.monthlyPence ?? 0,
          };
          setChoice(restoredChoice);
          if (resume.deliveryAddress) {
            setAddress(resume.deliveryAddress);
            setPrefill(resume.deliveryAddress);
          }
          const active: CheckoutSessionResult = {
            orderCaseId: resume.orderCaseId,
            subscriptionId: resume.subscriptionId,
            paymentSession: resume.paymentSession,
          };
          setSession(active);
          // The case exists and its monthly is STORED (see `caseMinted`), and
          // this session carries no acknowledgement from THIS window: the tick
          // re-POSTs it (see `sessionCarriesAck`).
          caseMinted.current = true;
          sessionCarriesAck.current = false;
          setOrdersPriceFixed(true);
          applyPublishableKeyForSession(active);
          // Old 'payment' resumes land on the MERGED step with the payment half
          // already revealed: the restored `address` (set above, and mirrored
          // into `prefill` for a later Change) is what opens it, collapsed. The
          // safety tick is still re-asked — see the gate's own resume spec.
          setStage('details');
          if (refusalNotice) {
            setCheckoutNotice(refusalNotice);
            noticeFocusPending.current = true;
          }
          setCheckoutRestore('ready');
          return;
        }
        if (resume.status === 'plan_selected' && resume.productKey && resume.months) {
          // ⚠️ `resume.monthlyPence` here is the SELECTION-time quote, and it
          // can be stale: orders re-quotes this draft at session mint and holds
          // the price of THAT moment. It is kept as the quote the patient chose
          // at (the card step says so when the live price differs), and never
          // printed as the amount — see `checkout/cardStepPrice.ts`.
          setChoice({
            productKey: resume.productKey,
            months: resume.months,
            medicationName: resume.medicationName ?? 'Your treatment',
            monthlyPence: resume.monthlyPence ?? 0,
          });
          // Old 'address' resumes land on the merged step too, in its
          // address-first sub-state: no saved address yet, so only the form
          // shows and the payment half stays withheld until it is saved.
          setStage('details');
          setCheckoutRestore('ready');
          return;
        }
        if (resume.status === 'complete' && resume.orderCaseId && resume.subscriptionId) {
          setChoice({
            productKey: resume.productKey ?? '',
            months: resume.months ?? 1,
            medicationName: resume.medicationName ?? 'Your treatment',
            monthlyPence: resume.monthlyPence ?? 0,
          });
          if (resume.deliveryAddress) setAddress(resume.deliveryAddress);
          const resumedPhase = resume.paymentPhase === 'captured' ? 'captured' : 'held';
          setAttachResult({
            orderCaseId: resume.orderCaseId,
            subscriptionId: resume.subscriptionId,
            paymentPhase: resumedPhase,
          });
          if (accountReady) {
            // A REFRESH ONTO A FINISHED ORDER IS NOT A NEW CONVERSION. The order
            // completed earlier — possibly days ago — so it lands on the same
            // confirmation route but with `justCompleted: false`, and the
            // front-end conversion event does not fire. Sending them here rather
            // than rendering a second copy of the confirmation in this file keeps
            // ONE confirmation surface, at one URL, for one order.
            goToConfirmation({
              captureRoute: paymentPhaseToCaptureRoute(resumedPhase),
              medicationName: resume.medicationName ?? undefined,
              justCompleted: false,
            });
          } else {
            // Same rule on the account step: this branch never sets
            // `paymentPlacedHere`, so the email-hold hand-over later hands over
            // `justCompleted: false` for an order this document did not place.
            setStage('account');
          }
          setCheckoutRestore('ready');
          return;
        }
        // A clinician-built plan is NOT the chooser: its screen charges the
        // first month on confirmation, so the hold-route card and the
        // pre-plan hold are not this screen's (checkoutLoaderContent.ts).
        // Neither is a landing here with anything committed: only orders'
        // `none` is "nothing chosen yet" — an `awaiting_payment` or `complete`
        // missing its ids falls through to the plan too, and is never held.
        let clinicianBuilt = false;
        try {
          const preview = await getPlanPreview(assessmentId, opts);
          if (cancelled) return;
          if (preview.hasPlan && preview.productKey && preview.months) {
            clinicianBuilt = true;
            setClinicianPlan({
              productKey: preview.productKey,
              months: preview.months,
              medicationName: preview.medicationName ?? 'Your treatment',
              monthlyPence: preview.monthlyPence ?? 0,
            });
          }
        } catch {
          /* leave normal patient plan chooser */
        }
        if (!cancelled) {
          const onChooser = resume.status === 'none' && !clinicianBuilt;
          if (onChooser) {
            // The plan screen's reads start NOW, not when it mounts after the
            // pre-plan hold (see `planRead`). A stale answer — this restore
            // superseded before it landed — finds another handle and is dropped.
            const early = startPlanScreenRead(clinicSlug, assessmentId, opts?.checkoutToken);
            setPlanRead({ read: early, state: 'pending' });
            void early.result.then((read) =>
              setPlanRead((current) =>
                current?.read === early ? { read: early, state: planScreenShowsError(read) ? 'failed' : 'landed' } : current,
              ),
            );
          }
          setStage('plan');
          setRestoreLandedOnChooser(onChooser);
          setCheckoutRestore('ready');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setCheckoutRestore('error');
      });
    return () => {
      cancelled = true;
    };
    // ⚠ UNREVIEWED SUPPRESSION (2026-09-05, first time this file was ever linted).
    // exhaustive-deps wants `goToConfirmation` here. It is deliberately absent —
    // it closes over this render's `confirmRoute`/`choice`/`leadId` (see the note
    // at its definition) — but adding it would change when a PAYMENT effect
    // re-runs, so no drive-by fix was made. Behaviour is unchanged from before
    // the linter existed. Flagged for CTO review. (`clinicSlug` IS listed: the
    // chooser landing starts the plan screen's reads for it — `planRead`.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, authStatus, assessmentId, restoreNonce, checkoutToken, attachResult, accountReady, clinicSlug]);

  /**
   * Load the chosen treatment's safety content once the patient reaches Payment.
   *
   * Re-loading CLEARS the tick. Consent to one version's wording is not consent
   * to another's — the same reasoning that makes `chooseTerm` clear
   * `termsAccepted` when the commitment changes. Without it a patient could tick
   * against content that was then replaced under them, and the acknowledgement
   * we sent would name a version they never saw.
   */
  const safetyProductKey = choice?.productKey ?? null;
  useEffect(() => {
    if (stage !== 'details' || !safetyProductKey) return;
    let cancelled = false;
    setSafetyAccepted(false);
    setSafetyView({ state: 'loading' });
    // Cleared with the safety view so a product switch can never leave the
    // summary pricing the PREVIOUS treatment's ladder while the new read is in
    // flight — and while it is null the summary prints no money at all.
    setCatalogPlans(null);
    const opts = !isSignedIn && checkoutToken ? { checkoutToken, assessmentId } : undefined;
    getPlanOptions(clinicSlug, opts)
      .then((response) => {
        if (cancelled) return;
        const option = response.options.find((o) => o.productKey === safetyProductKey);
        // `ready` with `safety: null` is NOT the same as a failed read, and the
        // gate renders them apart. Both refuse the sale; only one has a retry.
        setSafetyView({ state: 'ready', safety: option?.safety ?? null });
        // The ladder rides the same response — a failed read simply leaves the
        // summary without money rows, never with an invented or stale figure.
        setCatalogPlans(option?.plans ?? null);
      })
      .catch(() => {
        if (!cancelled) setSafetyView({ state: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [stage, safetyProductKey, clinicSlug, assessmentId, isSignedIn, checkoutToken, safetyNonce]);

  const loadedSafety = safetyView.state === 'ready' ? safetyView.safety : null;
  /**
   * The acknowledgement to send with checkout — built from the content that was
   * RENDERED, and null until the patient has ticked. Null is the whole gate:
   * every checkout call site below refuses to fire without it, and
   * orders-service refuses the request without it, so a browser that skipped the
   * tick box cannot buy a prescription-only medicine unexplained.
   */
  const safetyAck = useMemo<SafetyAcknowledgement | null>(
    () => (loadedSafety && safetyAccepted ? safetyAckFor(loadedSafety) : null),
    [loadedSafety, safetyAccepted],
  );
  /** WHY the pay control is blocked, in words. Null ⇒ it is not blocked. */
  const payBlockedText = payBlockedReason(safetyView, safetyAccepted);
  const payBlocked = payBlockedText !== null;

  /**
   * THE MONEY AT CARD ENTRY — one answer, read by every figure on this screen:
   * the Monthly price row, the term total, the rail's "Held on your card today"
   * / "Paid today" headline (the only place the card step prints the month-1
   * figure since 2026-09-26 — `CardHoldExplainer` names none), and the `add_payment_info`
   * amount (CTO 2026-09-23: at the card the patient must see what they are
   * paying for and the amount).
   *
   * The SOURCE is the lane's, never a preference: on the hold route orders
   * re-quotes from prices-service when the session is minted, so the live
   * ladder is the figure it will hold; on the clinician-led plan, on a resumed
   * `awaiting_payment` session and after an Edit plan round trip on a case this
   * checkout already minted, orders has already fixed the price, so its own echo
   * is (`ordersPriceFixed`). The full rule, with the orders-service code it rests
   * on, is in `checkout/cardStepPrice.ts`. Null ⇒ print no money (the ladder is
   * still loading or failed, or there is no real price yet).
   *
   * ⚠️ NEVER `choice.monthlyPence * choice.months` anywhere on this screen —
   * the total is the ladder's `termPricing` figure or it is absent.
   */
  const cardQuoteSource: CardStepQuoteSource =
    previewRoute === 'immediate' || ordersPriceFixed ? 'orders' : 'ladder';
  const cardPrice = useMemo(
    () => cardStepPrice({ choice, ladder: catalogPlans, source: cardQuoteSource }),
    [choice, catalogPlans, cardQuoteSource],
  );

  /**
   * EDIT PLAN IS OFFERED ONLY WHILE CHANGING THE PLAN IS MONEY-SAFE (CTO
   * 2026-09-24; the session lifecycle it rests on is mapped in full in the
   * workstream's session report and restated here).
   *
   *  - HOLD ROUTE, PATIENT-CHOSEN PLAN ONLY. A clinician-led plan is preapproved:
   *    orders ignores the client's product and months for it, and any amendment
   *    is a clinician rebuild — there is nothing here for the patient to edit.
   *  - NOT WHILE `busy`. `initialising`: the session POST would land after the
   *    patient has left the screen. `finalising` / `finalising_stub`: the attach
   *    is placing the hold.
   *  - NOT WHILE `confirming`. Stripe's confirm has already captured this
   *    session in its closure.
   *  - NOT WHILE `editingAddress`. No money risk, but unmounting a changed
   *    address form silently discards the edit (its guard only blocks router
   *    navigation, and a stage change is not one). Belt and braces TODAY: the
   *    rail unmounts with the payment half while the form is open, so dropping
   *    this limb alone leaves every spec green (mutation-checked 2026-09-24). It
   *    is kept for the layout that one day keeps the rail on screen during
   *    Change — do not "tidy" it away because coverage stays green.
   *  - NEVER AGAIN once the card is confirmed (`cardSetupConfirmed`, sticky), or
   *    once an attach has answered.
   *
   * WITHHELD, NOT DISABLED: the rail simply has no control. The server is the
   * real control (R41) — orders refuses to re-plan a case with a month-1
   * pre-authorisation record or a succeeded SetupIntent, and the portal RE-READS
   * the order and says so in words when it does (`PLAN_CHANGE_REFUSED_MESSAGE`).
   */
  const canEditPlan =
    previewRoute === 'hold' &&
    clinicianPlan === null &&
    choice !== null &&
    busy === null &&
    !confirming &&
    !editingAddress &&
    !cardSetupConfirmed &&
    attachResult === null;

  function handleAssessmentGone(err: unknown): boolean {
    if (onAssessmentGone && err instanceof ApiError && (err.status === 404 || err.status === 409)) {
      onAssessmentGone();
      return true;
    }
    return false;
  }

  /**
   * RE-READ THE ORDER AFTER A REFUSAL — never rebuild the step from this
   * window's own state (journey UX review 2026-09-24, P-B).
   *
   * A 409 on the plan save, the session POST or the attach means the order moved
   * on the server while this window sat still: another window re-planned it,
   * paid for it, or cleared its acknowledgement. Everything this window holds
   * about the plan, the price, the session and the card may now be wrong, and
   * the first cut of Edit plan proved how: its 409 branch put the patient back
   * on a live pay step built from the local `choice` with no session, and the
   * tick then minted a SECOND order (and a second month-1 hold) on an orders
   * build without #245.
   *
   * So drop what belonged to the stale picture and run `checkout/resume` again
   * (`restoreNonce`), which renders whatever orders holds NOW — `complete` → the
   * account step, or the confirmation with `justCompleted: false` (and an
   * account step reached this way hands over `justCompleted: false` too when
   * the email link lands — `paymentPlacedHere`);
   * `awaiting_payment` → the server's plan, price and session; nothing → the plan
   * screen. `notice` waits in `pendingRefusalNotice` and is shown only if the
   * re-read lands back on the card step, so it is never said over a loader or
   * over the plan this window remembered.
   *
   * The tick goes (the next session POST must carry an acknowledgement given on
   * the re-read screen), and so does `cardSetupConfirmed`: after the re-read the
   * screen is the server's current order, exactly as after a hard refresh, and
   * orders refuses a re-plan whose card setup has begun whatever this flag says.
   */
  function rereadCheckout(notice: CheckoutNotice | null) {
    // The re-read's screen is the server's order now; an ownership hold was
    // about a step that no longer stands.
    setCheckoutHold(null);
    pendingRefusalNotice.current = notice;
    noticeFocusPending.current = false;
    setCheckoutNotice(null);
    setPlanEditFrom(null);
    setSession(null);
    setPublishableKey(null);
    sessionCarriesAck.current = false;
    setSafetyAccepted(false);
    autoPreparedSession.current = false;
    setCardSetupConfirmed(false);
    setAttachOutcomePending(false);
    setPayError(null);
    setRestoreNonce((n) => n + 1);
  }

  /**
   * A session or attach refusal that means this window is STALE — see
   * `staleCheckoutRefusal` for the four — is answered by `rereadCheckout`, and
   * BEFORE `handleAssessmentGone`: on the plan-link lane that helper turns every
   * 409 into "this link has expired", which is false for an order that simply
   * moved on in another window.
   */
  function rereadOnStaleRefusal(err: unknown): boolean {
    const refusal = staleCheckoutRefusal(err);
    if (!refusal) return false;
    rereadCheckout(STALE_REFUSAL_NOTICE[refusal]);
    return true;
  }

  /**
   * TRUE while the hold's retry runs, until that retry is refused again. The
   * retry clears the hold when it ends UNLESS this says orders refused it on
   * ownership again (`holdOnOwnershipRefusal`).
   */
  const ownershipRefusedThisRun = useRef(false);

  /**
   * ORDERS S0.4 REFUSED THIS SIGNED-IN STEP — show the hold, remember the step
   * (`HeldCheckoutStep`), and tell the caller to stop. Checked FIRST in every
   * authed lane's catch: before `rereadOnStaleRefusal` (a 403 is not a stale
   * window) and before `handleAssessmentGone` (on the plan-link lane that turns a
   * 404/409 into "this link has expired" — never the answer to these codes).
   * Anything else, including an older orders build with no S0.4, returns false
   * and keeps its existing handling. See `checkoutOwnership.ts`.
   */
  function holdOnOwnershipRefusal(err: unknown, step: HeldCheckoutStep): boolean {
    const refusal = checkoutOwnershipRefusal(err);
    if (!refusal) return false;
    ownershipRefusedThisRun.current = true;
    /**
     * ORDERS AND THIS SESSION DISAGREE ABOUT THE MAILBOX (review, 2026-09-26).
     * `me` here is the render that sent this step — for the hold's retry, the
     * one holding the session re-read just before it (see `runHeldStep`). orders
     * does not read `/auth/me`: its `isProvenMailbox` reads identity's
     * dispensing-profile, and answers "not proven" when that carries no
     * `emailVerified` (identity older than S0.3 — the rollback floor orders'
     * CLAUDE.md records), when the Patient row's email is blank, or when
     * identity is unavailable. Then "Confirm your email" is false, the "not
     * yet" line is false, and "Send the link again" sends nothing (identity's
     * resend returns early for a verified account) — a loop nothing the patient
     * does can end. So the hold says what is true instead, and this is logged
     * with no address and no id (R19): only which lane.
     */
    const accountSaysVerified = refusal === EMAIL_NOT_VERIFIED && me?.emailVerified === true;
    if (accountSaysVerified) {
      console.warn('[checkout] orders refused email_not_verified for an account whose session says verified', {
        lane: step.lane,
      });
    }
    setCheckoutHold((prev) => ({
      refusal,
      step,
      accountSaysVerified,
      // "Not yet" only when the SAME screen comes back on the retry; a first
      // refusal is not a "not yet", and a switch to `checkout_not_owner` — or
      // from the email hold to the account check — is a different screen.
      notYetConfirmed:
        prev !== null &&
        prev.refusal === EMAIL_NOT_VERIFIED &&
        refusal === EMAIL_NOT_VERIFIED &&
        prev.accountSaysVerified === accountSaysVerified,
    }));
    return true;
  }

  /**
   * THE HOLD'S SIGN OUT KEEPS THIS CHECKOUT (review, 2026-09-26). The screen's
   * way on is "sign in with that email", so sign-out lands on the SIGN-IN screen
   * carrying `?next=` back here — the router's own pathname and search
   * (basename-relative, as the 401 handler builds it), so the `?assessment=`
   * the address bar holds comes along and `safeNextForRole` lets a patient back
   * in. The plain `logout` sent a PATIENT to the dashboard and REPLACED the
   * checkout URL in history, and this account has no case on the assessment, so
   * nothing led back to it. Never the fragment: a `#resume=` grant stays off
   * any URL this page writes.
   */
  function signOutKeepingCheckout() {
    logout({ next: `${location.pathname}${location.search}` });
  }

  /**
   * "I’ve confirmed it — continue": re-read the session, then retry THE SAME
   * step. The retry does not run here — this closure still holds the old `me`,
   * and an attach that succeeds reads `accountReady` to choose between the
   * confirmation and the account step. It runs from the effect below, in the
   * render that holds the fresh session.
   */
  const heldRetryStep = useRef<HeldCheckoutStep | null>(null);
  const [holdRetryNonce, setHoldRetryNonce] = useState(0);
  async function continueAfterEmailConfirmed() {
    if (!checkoutHold || holdBusy) return;
    heldRetryStep.current = checkoutHold.step;
    setHoldBusy(true);
    await refresh();
    setHoldRetryNonce((n) => n + 1);
  }

  /** The latest render's retry — read by the effect, so it never runs a stale closure. */
  const runHeldStep = useRef<(step: HeldCheckoutStep) => Promise<unknown>>(async () => undefined);
  runHeldStep.current = async (step: HeldCheckoutStep) => {
    switch (step.lane) {
      case 'plan':
        return continueToAddress(step.next);
      case 'attach':
        // THE SAME SetupIntent, straight to the attach: the card was confirmed
        // at Stripe before the refusal, and orders answers a re-attach of the
        // same intent idempotently. Never a second `confirmSetup`.
        return finaliseWithSetupIntent(step.active, step.setupIntentId, step.handOver);
      case 'session':
        if (step.via === 'tick') return prepareSession(step.ack, step);
        if (step.via === 'pay') return startOrContinuePayment();
        return onSetupConfirmed(step.setupIntentId, step.handOver);
    }
  };

  useEffect(() => {
    const step = heldRetryStep.current;
    if (holdRetryNonce === 0 || !step) return;
    heldRetryStep.current = null;
    // The refusal was about THIS signed-in account. If the re-read left nobody
    // signed in (a failed `/auth/me`), re-sending the step would ask as somebody
    // else — the guest lane — which is not the same step. Stay on the hold; the
    // next press re-reads again.
    if (!isSignedIn) {
      setHoldBusy(false);
      return;
    }
    ownershipRefusedThisRun.current = false;
    void runHeldStep.current(step).finally(() => {
      setHoldBusy(false);
      if (!ownershipRefusedThisRun.current) setCheckoutHold(null);
    });
    // Only a press starts a retry: the step is taken (and cleared) above, so a
    // later `isSignedIn` change re-runs this and finds nothing to do.
  }, [holdRetryNonce, isSignedIn]);

  /**
   * Persist plan on Orders before painting the merged delivery-and-payment step.
   *
   * ON AN EDIT PLAN ROUND TRIP (`planEditFrom` set) the SERVER decides whether
   * the plan changed, and the screen shows the server's answer — never the
   * patient's pick on its own say-so:
   *  - orders re-plans an owned `awaiting_payment` case (orders-service
   *    `saveCheckoutSelection`, the Edit plan re-plan) and echoes the NEW plan;
   *  - an orders build that predates that re-plan echoes the OLD plan back: the
   *    step renders it WITH `PLAN_CHANGE_REFUSED_MESSAGE` — a silent snap-back
   *    would leave the patient believing they had bought what they picked;
   *  - a 409 (money committed, or a guard refused the re-plan) RE-READS the
   *    order (`rereadCheckout`) — see the catch below.
   *
   * THE PRICE AFTER AN EDIT PLAN ROUND TRIP (P-A). On a case this checkout has
   * already minted (`caseMinted`), orders' selection echo IS the monthly it will
   * hold — the stored first-mint figure when nothing changed, the
   * re-quoted-and-stored one after a re-plan — so the card step prints it
   * (`ordersPriceFixed`) and never the live ladder, which may have moved since
   * the mint. Before a mint the case is a draft that orders re-quotes at the mint,
   * so the ladder stays the source.
   */
  async function continueToAddress(next: PlanChoice) {
    if (planPersistBusy || placing) return;
    setPayError(null);
    setPlanPersistBusy(true);
    const editFrom = planEditFrom;
    try {
      const saved = await saveCheckoutSelection(
        {
          assessmentId,
          productKey: next.productKey,
          months: next.months,
        },
        guestOpts,
      );
      const chosen: PlanChoice = {
        productKey: saved.productKey,
        months: saved.months,
        medicationName: saved.medicationName || next.medicationName,
        monthlyPence: saved.monthlyPence,
      };
      // First visit: every persist is a (new) plan. Edit plan: only a server
      // echo that differs from the plan being edited is a change.
      const planChanged =
        editFrom === null || saved.productKey !== editFrom.productKey || saved.months !== editFrom.months;
      if (editFrom !== null) {
        const echoIsPick = saved.productKey === next.productKey && saved.months === next.months;
        if (echoIsPick) {
          setCheckoutNotice(null);
        } else {
          setCheckoutNotice({ at: 'plan', message: PLAN_CHANGE_REFUSED_MESSAGE });
          noticeFocusPending.current = true;
        }
        setOrdersPriceFixed(caseMinted.current);
        setPlanEditFrom(null);
      }
      setChoice(chosen);
      setStage('details');
      // C5 — add_to_cart AFTER the stage advance (and after a successful server
      // persist). Term-based SKU only — never medicationName (Part F). Tracking
      // is fire-and-forget: a GTM throw must not land in this catch. An Edit
      // plan round trip that changed nothing is not a second add-to-cart.
      if (leadId && planChanged) {
        trackAddToCart({ leadId, plan: toTrackingPlan(chosen) });
      }
    } catch (err) {
      // orders S0.4: a signed-in patient whose email is not confirmed, or whose
      // account is not the one this check was started with. Not a failure —
      // the hold says what to do, and its continue re-sends THIS pick.
      if (holdOnOwnershipRefusal(err, { lane: 'plan', next })) return;
      if (err instanceof ApiError && err.status === 409) {
        // orders will not put THIS plan on the case: money is committed to it,
        // or a guard refused the re-plan (orders-service `CheckoutPlanLockedError`
        // is this route's only 409). The screen must now show what orders holds,
        // not what this window remembers — so re-read, on EVERY lane, including a
        // first visit (`editFrom === null`). Deliberately NOT
        // `handleAssessmentGone`: on the plan-link lane that turns any 409 into
        // "this link has expired", which is false for a checkout in progress.
        rereadCheckout({ at: 'plan', message: PLAN_CHANGE_REFUSED_MESSAGE });
        return;
      }
      if (handleAssessmentGone(err)) return;
      const mapped = mapCheckoutApiError(err, 'Sorry — we could not save your plan. Please try again.');
      setPayError(mapped.message);
    } finally {
      setPlanPersistBusy(false);
    }
  }

  /**
   * The attach, and what its answer means.
   *
   * `handOver` says whose payment a success is. `'placed_here'` (every caller but
   * one) is the normal hand-over: this document placed the payment, so it sets
   * `paymentPlacedHere`, celebrates a guest and pushes the one `add_payment_info`.
   * `'revisit'` is `onSetupAlreadyConfirmed`'s re-attach of an intent this
   * document did NOT confirm (`setupConfirmedHere`): orders' 200 is then true
   * but says nothing about who paid, so the success RE-READS the order and lands
   * the way a refresh onto a finished order does — `justCompleted: false`, no
   * celebration, no event. Every failure is handled the same on both.
   */
  async function finaliseWithSetupIntent(
    active: CheckoutSessionResult,
    setupIntentId: string,
    handOver: HandOver = 'placed_here',
  ) {
    setBusy('finalising');
    // What a lost answer left open before this attempt — an ownership refusal
    // below tells us nothing new about it, so it is put back as it was.
    const outcomePendingBefore = attachOutcomePending;
    // This attach IS the check a lost answer asked for; its own answer decides
    // what Change may do next (see `attachOutcomePending`).
    setAttachOutcomePending(false);
    try {
      const res = await attachPaymentMethod(
        active.orderCaseId,
        { setupIntentId },
        guestOpts ? { ...guestOpts, assessmentId } : undefined,
      );
      if (handOver === 'revisit') {
        // The order is paid for (or has just been, by this request) on a card
        // set up in another window or before a reload. Show what orders holds
        // now; claim nothing for this window.
        rereadCheckout(null);
        return;
      }
      // orders answered THIS window's attach with a hold or a capture, so this
      // document placed the payment. That includes the idempotent 200 for a
      // retry of the SAME SetupIntent after a lost response. It is the ONLY
      // place a payment sets this; see `paymentPlacedHere`.
      paymentPlacedHere.current = true;
      setAttachResult(res);
      if (accountReady) {
        // Verified patient — nothing left to do, so this IS the end of checkout.
        // The capture route comes from the response we just got, not from state
        // that has not re-rendered yet.
        goToConfirmation({
          captureRoute: paymentPhaseToCaptureRoute(res.paymentPhase),
          medicationName: choice?.medicationName,
          justCompleted: true,
        });
      } else {
        setStage('account');
        // THE CELEBRATION, FOR A GUEST ONLY. A signed-in patient waiting on
        // their emailed link already HAS an account — "Finish setting up my
        // account" would be wrong for them — and their moment is /thank-you
        // once the link lands. The route is THIS response's `paymentPhase`, not
        // the render-time `confirmRoute` (see goToConfirmation for why); it
        // picks the modal's clinical words only. No figure is passed: the modal
        // prints no amount on any route (CTO 2026-09-24, v2).
        if (!isSignedIn) {
          celebration.open(paymentPhaseToCaptureRoute(res.paymentPhase));
        }
      }
      // C7 — add_payment_info AFTER payment succeeds (account may follow for guests).
      // orderId MUST be active.orderCaseId: same id orders later sends as GA4
      // transaction_id on purchase. authorisedAmount is the month-1 HOLD (monthly)
      // — the SAME `cardPrice` figure the card screen stated, never the choice's
      // quote, which is stale after a reprice on a resumed draft. No figure on
      // screen ⇒ 0, never a number the patient was not shown.
      //
      // THE 3D SECURE RETURN IS COUNTED TOO (2026-09-25): it is this window's
      // payment, but a full page load lost the `leadId`, so it is pushed without
      // one (`returnedFromBank`). Every other lane without a lead id is
      // unchanged: no push.
      if (choice && (leadId || returnedFromBank.current)) {
        const monthlyPence = cardPrice?.monthlyPence ?? 0;
        trackAddPaymentInfo({
          leadId,
          orderId: active.orderCaseId,
          plan: toTrackingPlan({ months: choice.months, monthlyPence }),
          authorisedAmount: monthlyPence / 100,
        });
      }
    } catch (err) {
      // orders S0.4 refused THIS request before any Stripe call of its own
      // (after Phase A, before the hold — verified on orders dev f35b171). It
      // says nothing about an EARLIER attempt's hold, so the hold screen says
      // nothing about money, `attachOutcomePending` keeps what it was, and the
      // continue re-attaches the SAME SetupIntent — no second Stripe confirm.
      if (holdOnOwnershipRefusal(err, { lane: 'attach', active, setupIntentId, handOver })) {
        setAttachOutcomePending(outcomePendingBefore);
        return;
      }
      // An attach orders refused because the order moved on in another window
      // (no acknowledgement on the case, a SetupIntent the case no longer
      // holds, or an order already placed) — re-read it; see `rereadCheckout`.
      if (rereadOnStaleRefusal(err)) return;
      if (handleAssessmentGone(err)) return;
      // NO ANSWER FROM ORDERS IS NOT "NOT CHARGED" (lost attach response, journey
      // UX review 2026-09-24). A dropped connection or a 5xx may sit on top of an
      // attach orders completed — the hold is placed at Stripe before the ledger
      // is written — so the old copy ("Your card has not been charged. Please try
      // again.") could be false and sent patients to a second card.
      //
      // DECIDED: say we could not confirm it, and let the patient's next press of
      // the same control be the check. NOT a re-read (`checkout/resume`), because
      // the resume cannot answer the question that matters here: its `complete`
      // looks the same whether THIS window's card paid or another window's did,
      // so landing through it would hand over `justCompleted: false` (and no
      // celebration, no `add_payment_info`) for a payment this window made — and
      // on the not-landed branch it would clear the tick and make the patient
      // acknowledge again for nothing. Re-attaching the SAME SetupIntent is the
      // one request orders answers precisely: 200 → it is this window's payment
      // (the normal hand-over, counted once); the "already" 409 → another
      // window's (re-read); not landed → orders places the hold now. It is
      // idempotent on orders' side, and it is exactly what the next press does:
      // the stub lane re-attaches the session it holds, and the Elements lane
      // gets Stripe's "already succeeded" answer (`onSetupAlreadyConfirmed`).
      if (attachOutcomeUnknown(err)) {
        setPayError(payCopy.payOutcomeUnknown);
        // The hold may stand on the address already sent: no Change until the
        // next press has asked orders (see `attachOutcomePending`).
        setAttachOutcomePending(true);
        return;
      }
      // THE BANK SAID NO — a KNOWN answer, nothing held (2026-09-25). "Try a
      // different card" has to be something the patient can do, and on this
      // intent it is not: see `offerDifferentCard`.
      if (isDeclinedMonth1Hold(err)) {
        await offerDifferentCard(active, setupIntentId);
        return;
      }
      const mapped = mapCheckoutApiError(err, payCopy.payError);
      setPayError(mapped.message);
    } finally {
      setBusy(null);
    }
  }

  /**
   * A DIFFERENT CARD AFTER A DECLINED MONTH-1 HOLD (2026-09-25).
   *
   * THE DEAD END. The bank refuses the hold AFTER Stripe confirmed the card, so
   * the SetupIntent has `succeeded` and Stripe.js will not confirm another card
   * on it: the patient typed a new card, pressed, met Stripe's "already
   * succeeded" (`onSetupAlreadyConfirmed`), and the re-attach of the SAME intent
   * met the same decline — every press, under a sentence telling them to try a
   * different card. Edit plan was withheld too (`cardSetupConfirmed`).
   *
   * THE WAY OUT IS ORDERS'. `retry-card` rotates the case onto a NEW SetupIntent
   * (and a new month-1 hold key), but only after a decline orders RECORDED
   * itself — "no hold on record" is also what a hold whose ledger write failed
   * looks like, and a second card there would be a second hold. The answer is a
   * session, so the form REMOUNTS on it through the existing
   * `key={elementsClientSecret}`, exactly as after a re-plan's rotation, and the
   * declined copy stays above it — now true, because the form can take a card.
   *
   *  - The acknowledgement stays (`sessionCarriesAck`): same case, same medicine,
   *    and orders keeps it through the rotation.
   *  - `cardSetupConfirmed` and `setupConfirmedHere` clear: nothing is confirmed
   *    on the new intent, and orders allows a re-plan on it.
   *  - A stale refusal ("does not match" — another window rotated or re-planned;
   *    "already" — another window paid) re-reads, like every other lane here.
   *  - any other refusal (`cardRetryUnavailable`): orders' "contact us" 409 (no
   *    recorded decline, not awaiting payment, the cards-per-order cap, money
   *    standing unrecorded), a 404 from an orders build without the route, the
   *    guest lane's 401/403: the card was declined and no other card can be taken
   *    online, so say that instead of "try a different card".
   *  - Anything else (a 5xx, no answer): the decline copy stays. The next press
   *    re-attaches the declined intent (`onSetupAlreadyConfirmed`), meets the
   *    same decline and asks again — so it heals without a special path.
   */
  async function offerDifferentCard(active: CheckoutSessionResult, declinedSetupIntentId: string) {
    setBusy('initialising');
    try {
      const next = await retryCardAfterDecline(
        active.orderCaseId,
        { setupIntentId: declinedSetupIntentId },
        guestOpts ? { ...guestOpts, assessmentId } : undefined,
      );
      setupConfirmedHere.current = null;
      setCardSetupConfirmed(false);
      setSession(next);
      const flow = decidePaymentSessionFlow(next.paymentSession).kind;
      if (flow === 'invalid_real_session') {
        setPublishableKey(null);
        setPayError(PAYMENT_UNAVAILABLE_MESSAGE);
        return;
      }
      // A missing publishable key has already said "payments unavailable"; the
      // declined copy must not paint over it.
      if (!applyPublishableKeyForSession(next) && flow === 'elements') return;
      setPayError(DECLINED_TRY_ANOTHER_CARD_MESSAGE);
    } catch (err) {
      if (rereadOnStaleRefusal(err)) return;
      setPayError(cardRetryUnavailable(err) ? DECLINED_NO_DIFFERENT_CARD_MESSAGE : DECLINED_TRY_ANOTHER_CARD_MESSAGE);
    }
  }

  /**
   * Create the Orders checkout session — WITHOUT paying anything.
   *
   * THE ACKNOWLEDGEMENT IS A REQUIRED ARGUMENT, not an optional field read off
   * some outer state, because this is the single call that mints the OrderCase.
   * orders-service rejects a checkout that carries none (it will not create an
   * order for a prescription-only medicine nobody was warned about), so the type
   * signature makes the same refusal at the only place in this file that could
   * get it wrong.
   *
   * Stub ONLY when Orders returns `stub: true`. Real sessions require a valid
   * SetupIntent clientSecret — missing/blank/malformed secrets fail closed and
   * never fall through to stub finalisation. Callers reuse `session` so the UI
   * cannot mint duplicates; Orders also reuses awaiting_payment sessions.
   *
   * A SUCCESS MARKS TWO THINGS (review, 2026-09-24): the case now exists with a
   * STORED monthly (`caseMinted`), and the session in state carries THIS
   * window's acknowledgement (`sessionCarriesAck`). A 409 saying the plan was
   * changed in another window — or that the order is already placed — re-reads
   * the order instead of leaving a stale pay step up (`rereadCheckout`).
   */
  async function prepareSession(
    ack: SafetyAcknowledgement,
    /**
     * What an ownership hold (orders S0.4) should re-run once the patient has
     * confirmed their email: by default this POST alone (the tick's
     * auto-prepare); the pay press and the post-confirm attach pass themselves,
     * so the retry finishes what the press started.
     */
    heldAs: HeldCheckoutStep = { lane: 'session', via: 'tick', ack },
  ): Promise<CheckoutSessionResult | null> {
    if (!choice || !address) return null;
    setBusy('initialising');
    try {
      const active = await checkoutSession(
        {
          assessmentId,
          productKey: choice.productKey,
          months: choice.months,
          deliveryAddress: address,
          safetyAck: ack,
        },
        guestOpts,
      );
      caseMinted.current = true;
      sessionCarriesAck.current = true;
      setSession(active);
      if (decidePaymentSessionFlow(active.paymentSession).kind === 'invalid_real_session') {
        setPublishableKey(null);
        setPayError(PAYMENT_UNAVAILABLE_MESSAGE);
        return active;
      }
      applyPublishableKeyForSession(active);
      return active;
    } catch (err) {
      // orders S0.4, first thing in `initialiseCheckoutSession` — no case, no
      // write, no Stripe call. `payError` stays clear, so the one-shot
      // auto-prepare does not read this as a failure it must not repeat; the
      // hold's continue is what repeats it.
      if (holdOnOwnershipRefusal(err, heldAs)) return null;
      if (rereadOnStaleRefusal(err)) return null;
      if (handleAssessmentGone(err)) return null;
      const mapped = mapCheckoutApiError(err, payCopy.payError);
      setPayError(mapped.message);
      return null;
    } finally {
      setBusy(null);
    }
  }

  /**
   * The card form appears as soon as the risks are acknowledged — so ticking the
   * box costs the patient nothing and the funnel keeps its single pay press.
   *
   * WHY AN EFFECT RATHER THAN THE OLD CALL ON "Continue to payment": the session
   * POST now carries the acknowledgement, and the acknowledgement does not exist
   * until the patient has read the gate on THIS screen. Firing it at the address
   * step would either send an acknowledgement nobody gave — which is forgery on
   * a clinical record — or earn a 400 and break every checkout. So it moves to
   * the moment the tick lands. No money moves here: `checkout/session` creates
   * the OrderCase and a SetupIntent grant and takes no hold.
   *
   * `autoPreparedSession` fires this ONCE. A failure surfaces `payError` and
   * recovery is the patient's own press on the pay control (which retries
   * through `startOrContinuePayment`); re-arming the effect on error would spin
   * a retry loop against a server that is already saying no.
   *
   * A SESSION FROM `checkout/resume` DOES NOT COUNT (review, 2026-09-24): it
   * carries no acknowledgement from this window, and after a product re-plan
   * the case carries none at all. So the tick POSTs the session once on the
   * resumed lane too — orders reuses the same case and records the
   * acknowledgement (`sessionCarriesAck`).
   */
  const autoPreparedSession = useRef(false);
  useEffect(() => {
    // `editingAddress`: never mint while the address form is open — the POST
    // writes the address, and the one on screen is not settled yet.
    if (
      stage !== 'details' ||
      (session && sessionCarriesAck.current) ||
      !safetyAck ||
      !address ||
      editingAddress ||
      placing ||
      payError
    ) {
      return;
    }
    if (autoPreparedSession.current) return;
    autoPreparedSession.current = true;
    void prepareSession(safetyAck);
    // `prepareSession` is intentionally not a dependency: it closes over the
    // render's `choice`/`address`/`guestOpts`, which the guards above have
    // already checked, and adding it would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, session, safetyAck, address, editingAddress, placing, payError]);

  /**
   * The pay press. Reuses the session only when THIS window minted it with its
   * acknowledgement (the tick's auto-prepare); otherwise — no session yet, or one
   * handed back by `checkout/resume` — POSTs the session first, so orders has
   * this window's acknowledgement on the case before the attach. Then finalises
   * the stub path.
   *
   * The `safetyAck` guard is belt-and-braces: the control is already blocked
   * without it and the server refuses without it. It is here so that no future
   * caller of this function can pay around the gate by mistake.
   */
  async function startOrContinuePayment() {
    if (!choice || !address || editingAddress || placing || !safetyAck) return;
    setPayError(null);

    const active =
      session && sessionCarriesAck.current ? session : await prepareSession(safetyAck, { lane: 'session', via: 'pay' });
    if (!active) return;

    const decision = decidePaymentSessionFlow(active.paymentSession);
    if (decision.kind === 'stub') {
      setPublishableKey(null);
      // The stub lane's stand-in for a confirmed card — set before the attach,
      // exactly as `onSetupConfirmed` does (see `cardSetupConfirmed`).
      setCardSetupConfirmed(true);
      setBusy('finalising_stub');
      await finaliseWithSetupIntent(active, decision.setupIntentId);
      return;
    }
    if (decision.kind === 'invalid_real_session') {
      setPublishableKey(null);
      setPayError(PAYMENT_UNAVAILABLE_MESSAGE);
      setBusy(null);
      return;
    }

    applyPublishableKeyForSession(active);
    setBusy(null);
  }

  /**
   * The address is saved — COLLAPSE the form into the read-only "Deliver to"
   * block and reveal the payment half of the merged step. This used to be the
   * 'address' → 'payment' stage move; since 2026-09-23 both halves share one
   * screen and this only updates state, so the reveal is progressive
   * disclosure rather than a navigation.
   *
   * IT STILL DOES NOT CREATE THE CHECKOUT SESSION, and that is the point: the
   * OrderCase is not minted until the patient has been shown this medicine's
   * risks and said so. The cost is a small, deliberate one — between here and
   * the tick the typed address lives only in the browser, so a hard refresh
   * before acknowledging returns to the address-first sub-state with the
   * profile prefill instead of resuming a half-made order. Losing a typed
   * address is recoverable in thirty seconds; an order taken for a
   * prescription-only medicine nobody explained is not.
   */
  function confirmDelivery(nextAddress: Address) {
    if (!choice || placing || confirming) return;
    /**
     * A CORRECTED ADDRESS MUST REACH ORDERS. `POST checkout/session` is the only
     * call that writes `deliveryAddress` onto the OrderCase — nothing later
     * re-reads the address the browser is holding. Since the session POST moved
     * behind the safety tick, the auto-prepare above skips it whenever a session
     * already exists, so a patient who edited their address in place and saved
     * it would have had the new address rendered back at them on screen while
     * the medicine shipped to the old one.
     *
     * Dropping the session makes the tick mint a fresh one carrying the address
     * they just typed; orders reuses the same OrderCase and updates its address,
     * so this costs a request and creates nothing extra. The tick is cleared in
     * the same breath so the new session cannot be minted off the previous
     * acknowledgement — the POST that carries the address must be the one the
     * patient just authorised.
     *
     * NO PAYMENT CAN STRADDLE THIS. The form this submit comes from only exists
     * while no pay control does (`editingAddress` withdraws the payment half),
     * and Change — the only way to reopen it over a saved address — is locked
     * while any payment work is in flight: the session POST, the attach, AND
     * Stripe's `confirmSetup` (`confirming`, lifted from PaymentElementsForm),
     * AND while an attach's answer was lost (`attachOutcomePending`), when a hold
     * may already stand. Until 2026-09-23 this comment claimed the dirty flag
     * alone closed that window. It did not: a confirm pressed before the edit
     * carried the OLD session into its `onConfirmed` closure and finalised the
     * order against the old address while the screen showed the new one. Until
     * 2026-09-25 it said the same of the in-flight lock, and a lost attach left
     * Change live over a hold that stood (review, PROBE 1).
     */
    if (session && !sameDeliveryAddress(address, nextAddress)) {
      setSession(null);
      setSafetyAccepted(false);
    }
    setAddress(nextAddress);
    setEditingAddress(false);
    setAddressDirty(false);
    pendingAddressFocus.current = 'saved';
    setPayError(null);
    // Re-arm the one-shot auto-prepare. Its guard exists to stop a failed POST
    // retrying itself in a loop, NOT to punish a patient who edited their
    // address — saving it again is a fresh attempt, and without this reset the
    // second save would silently lose the tick-creates-the-session behaviour
    // and leave them pressing pay twice.
    autoPreparedSession.current = false;
  }

  /**
   * "Change" on the collapsed delivery address: re-open the form over the saved
   * address and WITHDRAW the payment half until it is saved again. Nothing is
   * dropped here — whether the session and the tick survive is decided on the
   * save, by `sameDeliveryAddress` in `confirmDelivery`, so re-saving the same
   * address costs the patient nothing.
   *
   * The guard mirrors the control's `disabled`: a Change that could start while
   * a confirm or attach is running — or after an attach whose answer was lost —
   * is the race this whole collapse exists to close, so the function refuses it
   * too rather than trusting the button.
   * A stale payment error is cleared with the half it belonged to.
   */
  function openAddressEditor() {
    if (placing || confirming || attachOutcomePending) return;
    setPayError(null);
    setAddressDirty(false);
    setEditingAddress(true);
    pendingAddressFocus.current = 'editor';
  }

  /**
   * EDIT PLAN (CTO 2026-09-24): back to the plan screen with the current choice
   * preselected. Offered only while `canEditPlan` — and re-checked here, because
   * a control that could fire inside a payment window is the race the gate
   * exists to close.
   *
   * ⚠️ EVERYTHING BELOW IS CLEARED IN THIS ONE UPDATE, AND THE FIRST LINE IS THE
   * ONE THAT MATTERS MOST. The acknowledgement is memoised from the loaded
   * safety content plus the tick, and the auto-prepare effect runs in the SAME
   * commit as the safety effect's reset on re-entering 'details' — before that
   * reset has taken hold. Left standing, the old tick (and the old content) would
   * mint a session for the NEW plan carrying the OLD medicine's acknowledgement,
   * and orders stores an ack without checking it against the product. So:
   *   - the tick and the safety content are cleared now, not on arrival;
   *   - the old ladder goes (`catalogPlans`), so the old price is never painted
   *     against the new plan;
   *   - the session and its key go, and the one-shot auto-prepare is re-armed, so
   *     the patient's NEW tick mints a session for the NEW plan in one press;
   *   - a stale payment error goes with the screen it was about.
   * The delivery ADDRESS stays: it rides the next session POST.
   */
  function editPlan() {
    if (!canEditPlan || !choice) return;
    setPlanEditFrom({ productKey: choice.productKey, months: choice.months });
    setCheckoutNotice(null);
    setSafetyAccepted(false);
    setSafetyView({ state: 'loading' });
    setCatalogPlans(null);
    setSession(null);
    setPublishableKey(null);
    autoPreparedSession.current = false;
    setPayError(null);
    setStage('plan');
  }

  /**
   * FOCUS FOLLOWS THE SWAP. Opening the form removes the Change button that had
   * focus, and saving removes the submit button that had it — either way the
   * keyboard would be dropped on <body>. Opening lands on the first field;
   * saving lands on the collapsed "Deliver to" block, which reads back what was
   * saved before the patient Tabs on into the payment half. Only after one of
   * those two presses: a resume that paints collapsed must not steal focus.
   */
  useEffect(() => {
    const target = pendingAddressFocus.current;
    if (!target) return;
    pendingAddressFocus.current = null;
    const id = target === 'editor' ? `${ADDRESS_ID_PREFIX}-line1` : SAVED_ADDRESS_ID;
    document.getElementById(id)?.focus();
  }, [editingAddress, address]);

  /**
   * Stripe confirmed the card in THIS document: remember the intent
   * (`setupConfirmedHere`), then attach it. The Elements form's `onConfirmed`,
   * and nothing else — `onSetupAlreadyConfirmed` must not reach this.
   */
  async function onCardConfirmedHere(setupIntentId: string) {
    setupConfirmedHere.current = setupIntentId;
    await onSetupConfirmed(setupIntentId);
  }

  async function onSetupConfirmed(setupIntentId: string, handOver: HandOver = 'placed_here') {
    if (!session || placing) return;
    setPayError(null);
    // The card is confirmed — before the attach, so a failed attach cannot
    // bring Edit plan back (see `cardSetupConfirmed`).
    setCardSetupConfirmed(true);
    // A RESUMED SESSION CARRIES NO ACKNOWLEDGEMENT FROM THIS WINDOW (see
    // `sessionCarriesAck`): POST it before the attach. On the normal path the
    // tick's auto-prepare has already done so and this is skipped; it runs here
    // only when that POST failed and the patient pressed the card form anyway.
    // The confirm cannot be pressed without the tick, so `safetyAck` is set.
    let active: CheckoutSessionResult | null = session;
    if (!sessionCarriesAck.current) {
      if (!safetyAck) return;
      active = await prepareSession(safetyAck, { lane: 'session', via: 'confirmed', setupIntentId, handOver });
      if (!active) return;
    }
    await finaliseWithSetupIntent(active, setupIntentId, handOver);
  }

  /**
   * STRIPE SAYS THIS CARD IS ALREADY SET UP — ASK ORDERS AGAIN, BUT ONLY FOR
   * THIS WINDOW'S OWN SETUPINTENT (lost attach response, journey UX review
   * 2026-09-24).
   *
   * `PaymentElementsForm` calls this instead of reporting an error when
   * `confirmSetup` is refused with `setup_intent_unexpected_state` on an intent
   * whose status is `succeeded` (`alreadySucceededSetupIntentId`). That is the
   * second press after an attach whose answer was lost — or whose request never
   * reached orders: the card was confirmed on the first press, so Stripe will not
   * confirm it again, and the attach is all that is left to ask.
   *
   *  - THE SAME INTENT AS THIS WINDOW'S SESSION, CONFIRMED IN THIS DOCUMENT
   *    (`setupConfirmedHere`) → attach it again, through `onSetupConfirmed`,
   *    i.e. exactly the path a first confirm takes (the acknowledgement check,
   *    `cardSetupConfirmed`, then `finaliseWithSetupIntent`). orders answers a
   *    retry of the SAME intent idempotently: 200 'held' when the first attach
   *    landed (no second hold), and it places the hold when it never did.
   *    Either way it is THIS window's payment, so the success branch there sets
   *    `paymentPlacedHere` and pushes the one `add_payment_info` — the lost
   *    attempt pushed nothing, because the events follow a successful attach.
   *    A stale 409 re-reads; any other error keeps its mapped message.
   *  - THE SAME INTENT, CONFIRMED ELSEWHERE → attach it too, but as a REVISIT
   *    (`'revisit'`: a success re-reads the order, `justCompleted: false`, no
   *    event). Two cases reach here. Another tab of this checkout (same session,
   *    same intent) confirmed the card and paid: orders' 200 is its payment, and
   *    counting it here would be the second conversion. Or this window
   *    confirmed it before a reload, and the attach never landed: the re-attach
   *    is what places the hold, and not attaching would strand the patient — the
   *    intent can never be confirmed again, and orders refuses a re-plan once a
   *    card is set up. That payment goes uncounted, which is the safe direction.
   *  - ANY OTHER INTENT → never attached. Stripe is talking about a card set up
   *    on an intent this window's session does not name, so this window cannot
   *    claim it; re-read the order instead and render what orders holds. The
   *    notice is the rotated-session one (`STALE_REFUSAL_NOTICE`): the intent
   *    this window confirmed is not the one its order now names, which only a
   *    re-plan does (orders O-A) — the same fact orders' own "does not match this
   *    payment session" 409 reports.
   *
   * Compared against `session` as this render holds it, which is the session the
   * card form was keyed on (`key={elementsClientSecret}`) when the patient pressed.
   */
  async function onSetupAlreadyConfirmed(setupIntentId: string) {
    if (!session || placing) return;
    if (session.paymentSession.setupIntentId !== setupIntentId) {
      rereadCheckout(STALE_REFUSAL_NOTICE.session_rotated);
      return;
    }
    await onSetupConfirmed(setupIntentId, setupConfirmedHere.current === setupIntentId ? 'placed_here' : 'revisit');
  }

  /**
   * BACK FROM THE BANK'S 3D SECURE PAGE — FINISH THE ORDER THE PATIENT
   * CONFIRMED THERE (prod defect, 2026-09-25; the whole story is in
   * `setupRedirectReturn.ts`).
   *
   * A redirect check takes the patient out of this document between Stripe's
   * `confirmSetup` and the attach that should follow it; the bank sends them back
   * to a FRESH document. The normal resume (`checkout/resume`) has already
   * re-read the order by the time this runs, so the card step, the plan, the
   * price and the session are the server's. Then:
   *  - ONLY THIS ORDER'S CARD. The return must name the SetupIntent orders holds
   *    for this case NOW (`redirectReturnAction`). Any other intent — the case
   *    was re-planned in another tab while the patient was at the bank, or the
   *    URL is not ours — is never attached: the screen stays the order as the
   *    resume just read it, which is `rereadCheckout`'s answer without a second
   *    read of the same thing.
   *  - `redirect_status=failed` → the ordinary declined sentence on the card
   *    step; the SetupIntent is back to taking a card.
   *  - `succeeded` → ASK STRIPE (`retrieveSetupIntent`, with the secret the
   *    return carried): the URL is a claim, the intent's status is the fact. Only
   *    `succeeded` on this very intent is attached — through
   *    `finaliseWithSetupIntent`, the SAME attach a card confirmed in place goes
   *    through, so orders places and records the hold exactly once (its attach
   *    is idempotent for the case's own SetupIntent, and refuses any other).
   *
   * THIS DOCUMENT IS THE PAYING WINDOW. The card was confirmed by this tab's own
   * `confirmSetup`, whose answer came back by redirect, so it is recorded as
   * confirmed here (`setupConfirmedHere` — a lost attach answer and a second
   * press then count as this window's payment, not as a revisit) and the
   * hand-over is `'placed_here'`: one `add_payment_info` (without `lead_id`,
   * see `returnedFromBank`) and, through the normal post-payment surface, one
   * `checkout_complete`. The address bar's return keys were scrubbed before the
   * page rendered, so a reload of the next screen replays none of it.
   *
   * NOT counted as this window's, and accepted: two documents that both read
   * `awaiting_payment` before either attached (a bank app that opens the return
   * in a second tab while the first follows the same redirect) both attach —
   * orders answers the second idempotently, with no second hold — and both count.
   *
   * The attach does NOT need the safety tick: the session this card was
   * confirmed against was minted with the acknowledgement before the patient
   * left (orders refuses a hold without one, and that refusal re-reads the order
   * like every other stale refusal here).
   */
  async function finishRedirectReturn(ret: SetupRedirectResult, active: CheckoutSessionResult) {
    try {
      const sessionIntent = active.paymentSession.setupIntentId;
      const action = redirectReturnAction(ret, sessionIntent);
      if (action === 'declined') {
        setPayError(mapStripePaymentError({ code: 'card_declined' }).message);
        return;
      }
      if (action !== 'verify' || !ret.clientSecret) return;
      if (decidePaymentSessionFlow(active.paymentSession).kind !== 'elements' || !publishableKey) return;
      let verdict: ReturnType<typeof redirectSetupVerdict> = 'unconfirmed';
      try {
        const stripe = await getStripePromise(publishableKey);
        const answer = stripe ? await stripe.retrieveSetupIntent(ret.clientSecret) : null;
        verdict = redirectSetupVerdict(answer?.setupIntent ?? null, sessionIntent);
      } catch {
        verdict = 'unconfirmed';
      }
      if (verdict === 'not_this_order') return;
      if (verdict !== 'attach') {
        // No attach was sent, so nothing is held — the ordinary in-place words
        // for the same outcome. `unconfirmed` is the network sentence: it claims
        // nothing about the card, and the next press of Place order asks again
        // (Stripe's "already succeeded" re-attaches this session's intent).
        setPayError(
          mapStripePaymentError(
            verdict === 'declined'
              ? { code: 'card_declined' }
              : verdict === 'authentication'
                ? { code: 'authentication_required' }
                : { type: 'api_connection_error' },
          ).message,
        );
        return;
      }
      setupConfirmedHere.current = ret.setupIntentId;
      returnedFromBank.current = true;
      // The session this card was confirmed against carried this tab's
      // acknowledgement to orders before the patient left (the tick's session
      // POST), so a second press after a lost answer re-attaches without POSTing
      // it again. Where orders holds none after all, its attach refusal re-reads
      // the order and this goes with it (`rereadCheckout`).
      sessionCarriesAck.current = true;
      // The card is set up — Edit plan is withdrawn from here, exactly as after
      // an in-place confirm (see `cardSetupConfirmed`).
      setCardSetupConfirmed(true);
      setPayError(null);
      await finaliseWithSetupIntent(active, ret.setupIntentId);
    } finally {
      setRedirectReturn(null);
      onPaymentReturnSettled?.();
    }
  }

  /**
   * Act on the return once the resume has settled on what orders holds: a card
   * step with a live session is the only screen a card set up at the bank can
   * finish. Anywhere else (the order already placed — perhaps by another tab —
   * back at the plan, or a restore error that is retried later) the return is
   * set aside and the screen is whatever the resume chose.
   *
   * A CLINICIAN-BUILT PLAN IS FINISHED HERE TOO, WHEN ORDERS HANDS IT BACK
   * (returning patients S7.2b, 2026-09-25). Its case stays `preapproved` until
   * the attach, and orders S7.2a answers the caller it is bound to with
   * `clinician_plan` and the case's own card session, which the resume above
   * restores to this card step with `clinicianPlan` set — so this effect runs
   * unchanged and the attach captures (Route 2). "Back at the plan" is still
   * where the return is set aside when orders does NOT hand it back: an orders
   * build without S7.2a (`none`), and every guest, because orders serves a
   * clinician plan only to the account bound to it and a guest principal never
   * binds one (S0.4). See `setupRedirectReturn.ts`.
   *
   * It waits for the plan-options read the safety effect makes on this step,
   * because that read is the price ladder `add_payment_info` states (the same
   * `cardPrice` the card screen printed). A failed read does not hold it up —
   * money is never gated on analytics.
   */
  useEffect(() => {
    if (!redirectReturn || redirectReturnTaken.current) return;
    if (authStatus === 'loading' || checkoutRestore !== 'ready') return;
    if (stage !== 'details' || !session) {
      redirectReturnTaken.current = true;
      setRedirectReturn(null);
      onPaymentReturnSettled?.();
      return;
    }
    if (safetyView.state === 'loading' && safetyProductKey) return;
    redirectReturnTaken.current = true;
    void finishRedirectReturn(redirectReturn, session);
    // `finishRedirectReturn` and `onPaymentReturnSettled` are deliberately not
    // dependencies: the ref makes this run once, and the function reads the
    // render it was started from (the session, key and price it checked).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectReturn, authStatus, checkoutRestore, stage, session, safetyView.state, safetyProductKey]);

  /**
   * "GETTING YOUR ORDER READY" — AND, ON ONE LANE, A SET LOADING TIME (CTO,
   * 2026-09-25: "Even for this loader that comes before selecting my plan …
   * Have a set loading time period."). The whole rule is in
   * `checkoutLoaderContent.ts`; here is the mechanism.
   *
   * `restoreWaiting` is the real wait (auth, then the restore read). The loader
   * shows while it lasts and, on the pre-plan lane only, a little after:
   *  - THE LANE (`preplanEntryWait`): JourneyPage said this checkout was opened
   *    from the Result screen this document submitted; no 3D Secure return is
   *    in hand (#458 — that document finishes an order, and its own loader says
   *    so); and the patient has not yet been shown a checkout screen (a later
   *    re-read is never held). Only this lane carries the journey card
   *    (`CHECKOUT_LOADER_CARD`: the rest of the journey, icon-led, revealed one
   *    item after another and then static — all of it inside the hold).
   *  - THE HOLD (`preplanHold`): on that lane, the read landed on the plan
   *    CHOOSER (orders `none`, no clinician-built plan). `useLoaderHold` then
   *    keeps the SAME loader — and the same card, mid-build or built — up
   *    until its mark has been drawn for `CHECKOUT_PREPLAN_MIN_DWELL_MS`,
   *    counted from the draw (after the 300ms show delay), so a read quicker
   *    than the delay drew nothing and is held for nothing.
   *  - THE HOLD RUNS ALONGSIDE THE PLAN SCREEN'S READS, NEVER IN FRONT OF THEM
   *    (release review, 2026-09-25 — `planScreenRead.ts`). The landing starts
   *    the plan options + recommendation reads (`planRead`) and, on the held
   *    lane, they are part of the wait (`planReadWaiting`): the loader goes at
   *    max(the set time from the draw, the reads landing), and the plan screen
   *    opens on their answer — no skeleton after the card. A read that FAILS
   *    releases at once (`planReadFailed`), so its error is never held
   *    (REQUIREMENTS A8). Before this, the reads began only when the plan
   *    screen mounted after the hold: measured, ~2.3s of dead time, a skeleton
   *    straight after the card, and an options error that could not show
   *    before ~3.35s.
   *  - EVERYTHING ELSE RELEASES AT ONCE (`release` = not the hold): an error,
   *    a redirect return, a resume onto delivery / payment / the account step,
   *    a paid or held order, any other committed state, a clinician-built
   *    plan, the signed-out landing with no read. Exactly the loader this was
   *    before: gone when the read is.
   * The end of the wait is worked out DURING render (the hook's contract), so
   * the plan screen never paints for a frame between the loader and its hold.
   *
   * ⚠️ TWO OF THESE LOCKS ARE SECOND LOCKS, AND STAY (mutation-probed,
   * CHANGELOG 2026-09-25). `redirectReturn === null`: a 3D Secure return's
   * wait is drawn by the "Finishing your order" loader above, which reports no
   * draw, so no hold can start there today — the guard is what keeps it so if
   * that loader ever shares the draw. `reassure={restoreWaiting ||
   * planReadWaiting}`: the hold ends ~3.3s after mount, before the 4s
   * still-working line, unless a slow first commit reports the draw late —
   * then the line must not say "working" over a wait that has ended (while the
   * plan reads are still out it IS a real wait, and may). Neither turns a spec
   * red when removed; neither is dead.
   */
  const restoreWaiting = authStatus === 'loading' || checkoutRestore === 'loading';
  const preplanEntryWait = preplanLoaderContent && redirectReturn === null && !checkoutScreenShown.current;
  const preplanHold = preplanEntryWait && checkoutRestore === 'ready' && restoreLandedOnChooser;
  // On the held lane the plan screen's reads are PART OF THE WAIT (they started
  // with the landing — `planRead`): the loader stays while they run, however
  // long, and one that fails lets it go at once.
  const planReadWaiting = preplanHold && planRead?.state === 'pending';
  const planReadFailed = preplanHold && planRead?.state === 'failed';
  const [showRestoreLoader, restoreMarkShown] = useLoaderHold(
    restoreWaiting || planReadWaiting,
    !preplanHold || planReadFailed,
    CHECKOUT_PREPLAN_MIN_DWELL_MS,
  );
  useEffect(() => {
    if (!showRestoreLoader && checkoutRestore === 'ready') checkoutScreenShown.current = true;
  }, [showRestoreLoader, checkoutRestore]);
  // The early read belongs to THIS visit to the plan screen: leaving it
  // (Continue, the first half of an Edit plan round trip) drops it, so a later
  // visit reads afresh. Staying on the plan stage keeps it for the screen that
  // mounts there — including after a 3D Secure return set aside onto it, whose
  // "Finishing your order" loader stands in front of the plan screen a while.
  useEffect(() => {
    if (stage !== 'plan') setPlanRead(null);
  }, [stage]);

  // While the return is being finished, the card step is not shown: the card
  // form was already filled in on the other side of the bank's page, and its
  // controls (and an unticked safety box) would only invite a second press.
  if (
    redirectReturn &&
    (authStatus === 'loading' || checkoutRestore === 'loading' || (checkoutRestore === 'ready' && stage === 'details'))
  ) {
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto', textAlign: 'center' }}>
        <BrandedLoader look="journey" message="Finishing your order" />
      </div>
    );
  }

  if (showRestoreLoader) {
    return (
      // The card lays its four steps side by side at >=880px and needs the
      // journey's whole 40rem column for it (tokens.css P11, as the entry
      // loader has); the plain mark and line keep the 32rem they always had.
      <div style={{ maxWidth: preplanEntryWait ? '40rem' : '32rem', margin: '0 auto', textAlign: 'center' }}>
        <BrandedLoader
          look="journey"
          message="Getting your order ready"
          card={preplanEntryWait ? CHECKOUT_LOADER_CARD : undefined}
          onShown={restoreMarkShown}
          // "Still working" claims a real wait — the restore, or the plan
          // screen's reads behind it. Through the rest of the set loading time
          // nothing is being waited on, so the line may not start there.
          reassure={restoreWaiting || planReadWaiting}
        />
      </div>
    );
  }

  if (checkoutRestore === 'error') {
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
        <ErrorState
          message="Sorry — we could not restore your checkout. Please try again."
          onRetry={retryCheckoutRestore}
        />
      </div>
    );
  }

  // ORDERS S0.4 SAID NOT YET (email) OR NOT YOU (a different address) — the
  // hold IS the screen while it stands (see `checkoutHold`). Its continue
  // re-reads the session and re-sends the refused step; nothing else on the
  // page can resend it in the meantime.
  if (checkoutHold) {
    return (
      <CheckoutOwnershipHold
        refusal={checkoutHold.refusal}
        accountSaysVerified={checkoutHold.accountSaysVerified}
        email={me?.email}
        notYetConfirmed={checkoutHold.notYetConfirmed}
        busy={holdBusy}
        onContinue={() => void continueAfterEmailConfirmed()}
        onSignOut={signOutKeepingCheckout}
        clinicSlug={clinicSlug}
        titleId={OWNERSHIP_HOLD_TITLE_ID}
        progress={<CheckoutProgress current={stage === 'details' ? 'details' : 'plan'} includeAccount={!accountReady} />}
      />
    );
  }

  if (stage === 'account') {
    /**
     * The claim state, rendered IDENTICALLY by both account-stage screens.
     *
     * By the time this can fail the patient has already been charged, and the
     * failure means their paid order did not attach to their account — the one
     * error on this journey that must never be silent. It carries its own retry
     * because the surrounding controls (register / sign in / confirm your email)
     * all look like they are for something else, so without it the patient's only
     * apparent move is to try to pay again.
     */
    /**
     * The bind held for the email (identity#105), said as what it is: a calm
     * `info` status, never the danger alert. On the "Confirm your email" screen
     * it takes the place of that screen's help line (the two said the same
     * thing, and this one is the true one for an order not yet on the account);
     * the screen's own link card and "I've confirmed" are the controls, and the
     * email-hold effect does the re-bind.
     */
    const bindHold = bindHeldForEmail ? (
      <Alert id={BIND_HOLD_ID} tabIndex={-1} tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
        {BIND_WAITS_FOR_EMAIL}
        {/* ON the email hold the link card below carries "Resend link": a
            sign-in sends no email and a link dies after 24 hours, so "the link
            we sent you" may be long gone. Its own element, so the hold line
            stays one matchable sentence. */}
        {onEmailHold ? (
          <>
            {' '}
            <span>{BIND_WAITS_RESEND_HINT}</span>
          </>
        ) : null}
        {/* OFF the email hold (only if `me` already says verified while identity
            still refused — the effect will not fire there), the patient needs
            a way to ask again, or the paid order sits unlinked in silence. */}
        {onEmailHold ? null : (
          <>
            {' '}
            <button type="button" style={inlineRetry} onClick={() => void finishAccountAndConfirm()}>
              Try again
            </button>
          </>
        )}
      </Alert>
    ) : null;
    const claimStatus = (
      <>
        {claimError ? (
          <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {claimError}{' '}
            <button type="button" style={inlineRetry} onClick={() => void finishAccountAndConfirm()}>
              Try again
            </button>
          </Alert>
        ) : null}
        {onEmailHold ? null : bindHold}
        {claimBusy ? <BrandedLoader message="Saving your order to your account" /> : null}
      </>
    );

    if (onEmailHold && me) {
      return (
        <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
          <CheckoutProgress current="account" includeAccount />
          {/* A focus target (tabIndex -1, never a Tab stop) for a sign-in that
              lands here — see the `signInFocusPending` effect. */}
          <Title id={EMAIL_HOLD_TITLE_ID} tabIndex={-1}>
            Confirm your email
          </Title>
          {/* ONE surface, one message: the held bind REPLACES the help line
              rather than stacking a third "open the link" sentence on it. */}
          {bindHold ?? <Help>{accountStageHelp(confirmRoute, 'confirm_email')}</Help>}
          {/* THE CLAIM STATE HAS TO SURVIVE THIS BRANCH. Signing in to an
              existing unverified account after paying as a guest runs the bind
              and claim and lands here, so a branch that returned before these
              lines would swallow the one error that matters most on this
              screen: the patient has paid and their order did not attach to
              their account. Silence there reads as success. (A bind HELD for
              the email is not that error — it is `bindHold` above.) */}
          {claimStatus}
          <CheckEmailCard email={me.email} />
        </div>
      );
    }

    /**
     * THE SIGN-IN VIEW — reached ONLY from identity's register 409, never from a
     * link (CTO 2026-09-24: the account step is for new patients). The address
     * is the one that register carried, locked; the patient types a password,
     * and the bind (email match) + claim run exactly as the retired sign-in link
     * ran them. No step indicator: none of its steps apply to signing in.
     */
    if (authMode === 'existing') {
      return (
        <div ref={celebration.accountStepRef} style={{ maxWidth: '32rem', margin: '0 auto' }}>
          <CheckoutProgress current="account" includeAccount />
          <Title>{EXISTING_ACCOUNT_TITLE}</Title>
          <Help id={EXISTING_ACCOUNT_REASON_ID}>{EXISTING_ACCOUNT_REASON}</Help>
          {claimStatus}
          <Card>
            <LoginForm
              lockedEmail={existingEmail ?? undefined}
              passwordDescribedBy={EXISTING_ACCOUNT_REASON_ID}
              onSuccess={() => {
                signInFocusPending.current = true;
                void finishAccountAndConfirm({ bindGrant: true });
              }}
            />
          </Card>
          {/* A NEW TAB, deliberately: this checkout lives in this tab's memory
              and the order it is about to link is already paid for. Resetting
              the password over there and coming back to sign in here keeps it —
              and the line under the link SAYS so, because the reset tab ends on
              its own "Sign in" that goes to the dashboard and links nothing. */}
          <p style={{ marginTop: 'var(--gps-s-3)', marginBottom: 'var(--gps-s-1)' }}>
            <ZoneExitLink
              to="/auth?mode=forgot"
              target="_blank"
              rel="noopener noreferrer"
              style={forgotLink}
              aria-describedby={EXISTING_ACCOUNT_FORGOT_RETURN_ID}
              onClick={() => {
                forgotLinkUsed.current = true;
              }}
            >
              {EXISTING_ACCOUNT_FORGOT}
            </ZoneExitLink>
          </p>
          <Help id={EXISTING_ACCOUNT_FORGOT_RETURN_ID} style={{ fontSize: '14px' }}>
            {EXISTING_ACCOUNT_FORGOT_RETURN}
          </Help>
          {celebration.dialog}
        </div>
      );
    }

    const bootstrapReady = accountBootstrap.status === 'ready';
    const bootstrapLoading = accountBootstrap.status === 'loading';
    const bootstrapError = accountBootstrap.status === 'error';

    // Option C: creating an account adopts the completed assessment, so the
    // guest proves control of its email with a 6-digit code. The order depends
    // on how much we still know:
    //  • FORM-FIRST (in-flow): we still hold the journey email + DOB, so the
    //    register form renders first (email as a line, DOB not shown) with NO
    //    code in sight; pressing "Create account" reveals the inline gate
    //    (resolveToken), and on a correct code the pending register completes.
    //    The form stays mounted behind the gate so its in-flight submit + error
    //    handling survive.
    //  • CODE-FIRST (cold return via resume link — the DOB is gone): the gate
    //    comes first, then the server account-bootstrap prefills the form.
    // A restore-verified token already in hand from an upstream ResultScreen OTP
    // skips the gate; the public checkout grant never does.
    const formFirst = hasJourneyIdentity;
    const needsCodeGate = !hasJourneyIdentity && !verifiedToken;
    // THE TWO STEPS (CTO 2026-09-24), ONLY WHERE THEY ARE TRUE, IN THE ORDER THEY
    // ARE TRUE. A token proved upstream (`reEntryToken`) means no code step on
    // this screen at all, so no indicator rather than a one-step list. Otherwise
    // the lane picks the order: form-first is password → code, code-first is
    // code → password. A code verified in THIS flow (`verifiedToken`) moves it on.
    const accountLane: AccountLane | null = reEntryToken ? null : formFirst ? 'form-first' : 'code-first';
    const accountStep: AccountStepState = formFirst
      ? awaitingCode
        ? 2
        : verifiedToken
          ? 'done'
          : 1
      : verifiedToken
        ? 2
        : 1;

    return (
      <div ref={celebration.accountStepRef} style={{ maxWidth: '32rem', margin: '0 auto' }}>
        <CheckoutProgress current="account" includeAccount />
        <Title>Create your account</Title>
        {/* Route-aware (CTO 2026-09-24): "Payment is done" was false on the
            HOLD route, where a hold is placed and nothing is charged. And no
            "(or sign in)" any more — the sign-in choice left this screen. */}
        <Help>{createAccountHelp(confirmRoute)}</Help>
        {accountLane ? <AccountSteps lane={accountLane} current={accountStep} /> : null}
        {claimStatus}
        {formFirst ? (
          <>
            {/* The keyboard's landing place once the code is accepted (see the
                `awaitingCode` effect). Not a live region: focus reads it, and a
                second announcement of the same words would only be noise. */}
            {emailConfirmedHere && !awaitingCode ? (
              <p id={EMAIL_CONFIRMED_ID} tabIndex={-1} style={emailConfirmedLine}>
                <CheckIcon size={14} />
                {EMAIL_CONFIRMED_STATUS}
              </p>
            ) : null}
            {/* Kept mounted (hidden) while the gate shows — the register submit is
                awaiting resolveToken, so unmounting would strand it. */}
            <div style={{ display: awaitingCode ? 'none' : 'block' }}>
              <Card>
                <RegisterForm
                  variant="checkout"
                  reEntryToken={verifiedToken}
                  prefillEmail={journeyEmail}
                  dateOfBirth={journeyDob}
                  leadId={leadId}
                  resolveToken={verifiedToken ? undefined : resolveToken}
                  onExistingAccount={onExistingAccount}
                  onSuccess={() => {
                    void finishAccountAndConfirm();
                  }}
                />
              </Card>
            </div>
            {awaitingCode ? (
              <>
                <RestoreCodeEntry
                  assessmentId={assessmentId}
                  onVerified={onCodeVerified}
                  skipInitialSend
                  heading="Confirm your email"
                  intro={`Enter the 6-digit code we’ve emailed to ${journeyEmail} to finish creating your account.`}
                  deliveryHelp={CODE_DELIVERY_HELP}
                />
                {/* The only way back to the password pair once the box is showing
                    (the retired sign-in link used to double as this exit). */}
                <p style={{ marginTop: 'var(--gps-s-3)' }}>
                  <button type="button" onClick={backToPassword} style={linkButton}>
                    {BACK_TO_PASSWORD}
                  </button>
                </p>
              </>
            ) : null}
          </>
        ) : needsCodeGate ? (
          <RestoreCodeEntry
            assessmentId={assessmentId}
            onVerified={setVerifiedToken}
            heading="Confirm your email"
            intro="To set up your account and keep your assessment linked, enter the 6-digit code we’ve emailed to the address from your check."
            deliveryHelp={CODE_DELIVERY_HELP}
          />
        ) : (
          <Card>
            {bootstrapLoading ? (
              <BrandedLoader message="Loading your account details" />
            ) : bootstrapError ? (
              <ErrorState
                message="Sorry — we could not load your account details. Please try again."
                onRetry={retryAccountBootstrap}
              />
            ) : bootstrapReady ? (
              <RegisterForm
                variant="checkout"
                reEntryToken={verifiedToken}
                prefillEmail={accountBootstrap.email}
                dateOfBirth={accountBootstrap.dateOfBirth}
                leadId={leadId}
                onExistingAccount={onExistingAccount}
                onSuccess={() => {
                  void finishAccountAndConfirm();
                }}
              />
            ) : null}
          </Card>
        )}
        {celebration.dialog}
      </div>
    );
  }

  if (stage === 'plan') {
    // Clinician-led lane: a plan was already built + pre-approved for this
    // assessment — show it read-only rather than asking the patient to choose.
    // preapproved OrderCase already persists the plan — do not create plan_selected.
    if (clinicianPlan) {
      return (
        <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
          <CheckoutProgress current="plan" includeAccount={!accountReady} />
          <Title>Your clinician has built your plan</Title>
          <Help>
            Your clinician chose this treatment for you on your call. Continue to confirm delivery and payment —
            there&rsquo;s no separate approval step, so your plan starts as soon as you pay.
          </Help>
          <Card style={{ marginBottom: 'var(--gps-s-3)' }}>
            <SummaryRow label="Treatment" value={clinicianPlan.medicationName} />
            <SummaryRow label="Plan" value={`${clinicianPlan.months} month${clinicianPlan.months === 1 ? '' : 's'}`} />
            {clinicianPlan.monthlyPence > 0 ? (
              <SummaryRow label="Price" value={`${formatPence(clinicianPlan.monthlyPence)}/month`} />
            ) : null}
          </Card>
          <Button
            type="button"
            variant="cta"
            arrow
            onClick={() => {
              setChoice(clinicianPlan);
              setStage('details');
              // C5 — clinician-led lane skips PlanSelection; still count the
              // confirmed plan as add_to_cart AFTER the stage advance so a GTM
              // throw cannot kill Continue (term-based SKU, Part F).
              if (leadId) {
                trackAddToCart({ leadId, plan: toTrackingPlan(clinicianPlan) });
              }
            }}
          >
            Continue to delivery
          </Button>
        </div>
      );
    }
    // Wave 4 §2: the assessmentId lets the selector honour a clinician's
    // recommendation set (filtered options + the two-step chooser).
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
        <CheckoutProgress current="plan" includeAccount={!accountReady} />
        {payError ? (
          <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
            {payError}
          </Alert>
        ) : null}
        <PlanSelection
          clinicSlug={clinicSlug}
          assessmentId={assessmentId}
          offerDropOffReason={offerDropOffReason}
          checkoutToken={guestOpts?.checkoutToken}
          startKg={startKg}
          bmi={bmi}
          initialChoice={planEditFrom ?? undefined}
          earlyRead={planRead?.read}
          onSelect={(c) => {
            void continueToAddress(c);
          }}
        />
        {planPersistBusy ? (
          <p style={{ marginTop: 'var(--gps-s-3)', textAlign: 'center' }}>Saving your plan…</p>
        ) : null}
      </div>
    );
  }

  if (stage === 'details') {
    const busyLabel = busy === 'initialising' ? 'Preparing secure payment…' : payCopy.placingLabel;
    const elementsDecision =
      elementsReady && session ? decidePaymentSessionFlow(session.paymentSession) : null;
    const elementsClientSecret =
      elementsDecision?.kind === 'elements' ? elementsDecision.clientSecret : null;
    /**
     * THE PROGRESSIVE DISCLOSURE (CTO 2026-09-23): the payment half exists on
     * this same screen, below the delivery address, from the moment an address
     * is SAVED — and it is withdrawn again while the address form is re-opened
     * (Change). Withdrawn, not merely disabled: the old two-step flow's safety
     * came precisely from there being no pay control anywhere while the address
     * form was the thing showing, and that is still the rule. The
     * saved-address requirement is the SAME validation `continueToPayment`
     * enforced as a step gate — AddressForm only calls `confirmDelivery` on a
     * submit that passes it.
     */
    const paymentRevealed = address !== null && !editingAddress;
    /**
     * Change is locked while ANY payment work is in flight: the session POST
     * and the attach (`placing`) and Stripe's confirm (`confirming`). Starting
     * an address change inside that window is the race the collapse closes.
     * It stays locked after an attach whose answer was lost
     * (`attachOutcomePending`): the attempt has not finished — a hold may stand
     * on the address already sent — and the next press of Place order is what
     * finishes it. The same visible reason says so.
     */
    const changeLocked = placing || confirming || attachOutcomePending;
    const payErrorAlert = payError ? (
      <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
        {payError}
      </Alert>
    ) : null;

    /**
     * TWO COLUMNS ONCE THE PAYMENT HALF IS REVEALED, MIRRORING THE PLAN SCREEN
     * (CTO 2026-09-24) — `.gps-pay-*` in tokens.css. LEFT: Deliver to, the
     * safety gate, the card and Place order. RIGHT, sticky: the order summary
     * (today's figure leading), the hold explained, the trust cues, Edit plan.
     *
     * ⚠️ ONE DOM ORDER SERVES BOTH LAYOUTS, AND IT IS THE PHONE'S READING ORDER:
     * Deliver to → the order summary → the hold → the risks → the card → Place
     * order, so a patient on a phone sees what they pay BEFORE the card form. The
     * desktop columns are grid AREAS over that same order, never a reshuffled
     * DOM — the Tab key and a screen reader follow the DOM, and the pinned
     * relative order Deliver-to → payment error → gate → pay control holds in
     * both (`checkout.spec.tsx` (e)).
     *
     * ⚠️ THE WIDE SHELL IS A WIDTH, NEVER A TRANSFORM. `.gps-plan-breakout`
     * centres itself with `translateX`, and a transformed ancestor becomes the
     * containing block of every `position: fixed` descendant — the non-portaled
     * `DialogShell`s among them. `.gps-pay-shell--rail` centres with margins.
     *
     * Before the reveal (address first, or Change re-opened) the step stays the
     * familiar single centred column: one task, one form.
     */
    return (
      <div className={paymentRevealed ? 'gps-pay-shell gps-pay-shell--rail' : 'gps-pay-shell'}>
        <CheckoutProgress current="details" includeAccount={!accountReady} />
        <Title>Where should we deliver?</Title>
        <Help>{payCopy.addressHelp}</Help>
        {/* An error with no payment half on screen still has to be SEEN; with
            the half revealed it sits beside the pay control instead (below). */}
        {paymentRevealed ? null : payErrorAlert}
        <div className="gps-pay-layout">
          <div className="gps-pay-deliver">
            {paymentRevealed && address ? (
              /* THE SAVED ADDRESS, COLLAPSED. Read-only on purpose: while it is
                 collapsed there is no form, so there is no field to type into
                 mid-payment and no unsaved-changes guard that could block the
                 /thank-you navigation of an order that has just been placed. */
              <Card style={{ marginBottom: 'var(--gps-s-4)' }}>
                <div
                  id={SAVED_ADDRESS_ID}
                  role="group"
                  aria-labelledby={`${SAVED_ADDRESS_ID}-label`}
                  tabIndex={-1}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 'var(--gps-s-3)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ minWidth: 0, flex: '1 1 12rem' }}>
                    <p
                      id={`${SAVED_ADDRESS_ID}-label`}
                      style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: 'var(--gps-muted)' }}
                    >
                      Deliver to
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: '15px', lineHeight: 1.45, color: 'var(--gps-text)' }}>
                      {formatAddress(address)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={changeLocked}
                    aria-describedby={changeLocked ? CHANGE_LOCKED_REASON_ID : undefined}
                    onClick={openAddressEditor}
                    style={{ width: 'auto', flex: '0 0 auto' }}
                  >
                    Change<span className="gps-sr-only"> delivery address</span>
                  </Button>
                </div>
                {/* WHY it is locked, in words, tied to the control — never a bare
                    `disabled` (WCAG 1.4.1 / 3.3.1), the same structure as the pay
                    gate below. */}
                {changeLocked ? (
                  <p
                    id={CHANGE_LOCKED_REASON_ID}
                    style={{ fontSize: '13px', color: 'var(--gps-muted)', margin: '8px 0 0', lineHeight: 1.45 }}
                  >
                    You can change the address once this payment attempt finishes.
                  </p>
                ) : null}
              </Card>
            ) : (
              <Card style={{ marginBottom: 'var(--gps-s-4)' }}>
                {/* No await on submit — the payment half paints immediately below and
                    the session is created once the safety gate in it is satisfied,
                    so there is no "Preparing payment…" wait to label, and no `busy`:
                    the only save this form makes is its own, and it is instant.
                    (It used to take `busy={placing}`, which read "Saving…" while a
                    PAYMENT ran.) The submit label keeps saying where the press
                    LANDS the patient: on the payment half of this same screen. */}
                <AddressForm
                  initial={address ?? prefill}
                  idPrefix={ADDRESS_ID_PREFIX}
                  submitLabel="Continue to payment"
                  submitArrow
                  onSubmit={(a) => confirmDelivery(a)}
                  onDirtyChange={setAddressDirty}
                />
              </Card>
            )}
            {address !== null && editingAddress ? (
              /* Says WHY the payment half went away, in words — a section that
                 vanishes silently reads as a bug, and the patient's way onward
                 (save the address) has to be named (WCAG 3.3.1 in spirit).
                 role="status": orientation, never an alert. */
              <p role="status" style={{ fontSize: '14px', color: 'var(--gps-muted)', lineHeight: 1.5 }}>
                {addressDirty ? (
                  <>
                    You&rsquo;ve changed your delivery address. Press &ldquo;Continue to payment&rdquo; to save it
                    &mdash; the payment section returns once it&rsquo;s saved.
                  </>
                ) : (
                  <>
                    Update your delivery address, then press &ldquo;Continue to payment&rdquo; &mdash; the payment
                    section returns once it&rsquo;s saved.
                  </>
                )}
              </p>
            ) : null}
          </div>

          {paymentRevealed ? (
            <>
              <div className="gps-pay-payhead">
                <Title as="h2" size="24px">
                  Confirm and place your order
                </Title>
                <Help>{payCopy.paymentHelp}</Help>
              </div>
              {/* THE MONEY IS ON THE CARD SCREEN (CTO 2026-09-23: "whenever they
                  are adding the card, they should see what they are paying for
                  and what the amount is") — and since 2026-09-24 TODAY'S figure
                  leads it. Every figure is `cardPrice`, the amount orders will
                  hold or take; see `checkout/cardStepPrice.ts` for which server
                  that is on each lane. No money at all while the plan-options read
                  is in flight (the pay control is gated on it anyway) or at the
                  0-pence launch placeholder. */}
              <PaymentRail
                route={previewRoute}
                medicationName={choice?.medicationName ?? 'Your recommended treatment'}
                months={choice?.months ?? null}
                price={cardPrice}
                trustPaymentCue={payCopy.trustPaymentCue}
                onEditPlan={canEditPlan ? editPlan : undefined}
                planNotice={checkoutNotice?.at === 'plan' ? checkoutNotice.message : null}
              />
              <div className="gps-pay-pay">
                <Alert tone="info" style={{ marginBottom: 'var(--gps-s-3)' }}>
                  {payCopy.paymentAlert}
                  {/* The "this is a stub — no card is taken" reassurance is TRUE only on
                      non-prod, where the setup intent is stubbed. Leaking it into prod
                      would falsely tell a real patient their card won't be charged, so
                      it is gated on the SAME host predicate the StagingBadge uses
                      (isNonProdHost) rather than a second, drifting check. */}
                  {/* Stub reassurance only after Orders returns stub:true — never before session
                      and never when real Elements is in play (stage now runs Real Stripe). */}
                  {isNonProdHost() && session?.paymentSession.stub === true
                    ? ' On stage this payment step is a stub — no card is taken.'
                    : null}
                </Alert>
                {/* THE PAYMENT ERROR SITS BESIDE THE PAY CONTROL — directly above
                    the gate and the button, where the patient who just pressed it
                    is looking. The first merged cut moved it to the top of the
                    screen, above the address, so a decline announced itself out of
                    sight of the control it was about. role="alert" kept. */}
                {payErrorAlert}

                {/* THE SAFETY GATE, directly above the pay control (Reneal Patel,
                    2026-08-18). It is above and not below because a patient who has
                    already reached for the button has stopped reading. Both pay
                    controls below — the Elements confirm and the stub button — are
                    gated on it, so unticking after the card form has appeared blocks
                    the payment again rather than leaving a live button behind. */}
                {/* A refusal about the ACKNOWLEDGEMENT sits directly above the
                    gate it is about, and goes once the patient ticks it again. */}
                {checkoutNotice?.at === 'safety' ? (
                  <CheckoutNoticeBlock message={checkoutNotice.message} style={{ marginBottom: 'var(--gps-s-3)' }} />
                ) : null}
                <MedicationSafetyGate
                  view={safetyView}
                  accepted={safetyAccepted}
                  onChange={(accepted) => {
                    setSafetyAccepted(accepted);
                    if (accepted && checkoutNotice?.at === 'safety') setCheckoutNotice(null);
                  }}
                  onRetry={retrySafety}
                />

                {elementsClientSecret && publishableKey ? (
                  <PaymentElementsForm
                    // A NEW SetupIntent IS A NEW FORM. Stripe's <Elements> reads
                    // `clientSecret` once, at mount; a session re-POSTed after a
                    // re-plan in another window, or re-read after a refusal, can
                    // hand back a ROTATED intent (orders O-A), and a form left
                    // mounted on the old secret would confirm a card against an
                    // intent the case no longer holds.
                    key={elementsClientSecret}
                    publishableKey={publishableKey}
                    clientSecret={elementsClientSecret}
                    busy={placing}
                    blocked={payBlocked}
                    blockedReasonId={PAY_BLOCKED_REASON_ID}
                    confirmLabel={payCopy.placeOrderLabel}
                    confirmingLabel={busyLabel}
                    onConfirmed={(setupIntentId) => onCardConfirmedHere(setupIntentId)}
                    onAlreadyConfirmed={(setupIntentId) => onSetupAlreadyConfirmed(setupIntentId)}
                    onError={(message) => setPayError(message || null)}
                    onSubmittingChange={setConfirming}
                    // A redirect 3D Secure check comes back to a fresh page, so
                    // the address must name this checkout by itself (in the
                    // fragment — see setupRedirectReturn.ts).
                    returnUrl={checkoutReturnUrl(assessmentId)}
                  />
                ) : (
                  <Button
                    type="button"
                    variant="cta"
                    disabled={placing || payBlocked}
                    aria-describedby={payBlocked ? PAY_BLOCKED_REASON_ID : undefined}
                    onClick={() => void startOrContinuePayment()}
                  >
                    {placing ? busyLabel : payCopy.placeOrderLabel}
                  </Button>
                )}

                {/* WHY the button will not work, in words, tied to it by
                    `aria-describedby`. Never colour alone and never a bare `disabled`
                    with no explanation — the same structure PlanSelection uses for the
                    commitment gate (WCAG 2.1 AA 1.4.1, 3.3.1). */}
                {payBlockedText ? (
                  <p
                    id={PAY_BLOCKED_REASON_ID}
                    style={{ fontSize: '13px', color: 'var(--gps-muted)', marginTop: '8px', lineHeight: 1.45 }}
                  >
                    {payBlockedText}
                  </p>
                ) : null}
                {/* No "Back to delivery" any more: the delivery address is the top
                    of THIS screen, in DOM order before every payment control, and
                    its Change control is how it is corrected. */}
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  // Defensive: should not happen, but never render blank. The journey's
  // branded loading moment (Wave 6 §1), not a bare spinner.
  return (
    <div style={{ maxWidth: '32rem', margin: '0 auto', textAlign: 'center' }}>
      <BrandedLoader look="journey" reassure={false} />
      <p>
        <ZoneExitLink to="/patients" style={{ color: 'var(--gps-link)' }}>
          Go to your dashboard
        </ZoneExitLink>
      </p>
    </div>
  );
}

/**
 * Retry control INSIDE an alert. Inherits the alert's own colour rather than
 * taking the link token: `--gps-link` is contrast-checked against the page
 * surface, not against a danger tint, so a link-coloured control on a red panel
 * can fall under 4.5:1 (WCAG 1.4.3). Underlined so it does not rest on colour
 * alone to read as actionable (1.4.1).
 */
const inlineRetry: React.CSSProperties = {
  background: 'none',
  border: 0,
  color: 'inherit',
  font: 'inherit',
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
  fontWeight: 700,
};

/** The form-first "Back to change your password" control — a real button styled as a quiet link. */
const linkButton: React.CSSProperties = {
  background: 'none',
  border: 0,
  color: 'var(--gps-link)',
  font: 'inherit',
  fontSize: '14px',
  cursor: 'pointer',
  padding: 0,
  textDecoration: 'underline',
};

/**
 * The "your email is confirmed" line: the success TEXT token (AA on the page
 * background) with a tick, so the state is words and not colour alone. The
 * outline is left to the global focus style — the line takes focus.
 */
const emailConfirmedLine: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: 'var(--gps-success-text)',
  fontSize: '14.5px',
  fontWeight: 600,
  margin: '0 0 var(--gps-s-3)',
};

/** The sign-in view's "Forgotten your password?" link — the link token, underlined (not colour alone). */
const forgotLink: React.CSSProperties = {
  color: 'var(--gps-link)',
  fontSize: '14px',
  textDecoration: 'underline',
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--gps-s-3)', padding: '6px 0', fontSize: '14.5px' }}>
      <span style={{ color: 'var(--gps-muted)' }}>{label}</span>
      <span style={{ color: 'var(--gps-text)', fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
